import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { adminAPI, tutorialsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Shield,
  Users,
  FileText,
  Loader2,
  Edit2,
  Save,
  Search,
  Flame,
  Bell,
  User,
  Cookie,
  Settings2,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Globe,
  Zap,
  Key,
  Upload,
  Download,
  Sparkles,
  Megaphone,
  Image,
  Phone,
  MessageCircle,
  Lock,
  Unlock,
  Clock,
  Edit3,
  Info,
  Crown,
  Coins,
  LayoutDashboard,
  UserPlus,
  History,
  Cpu,
  CreditCard,
  BookOpen,
  Database,
  ThumbsUp,
  Share2,
  Bot,
  Video,
  FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import LivestreamManager from '../components/admin/LivestreamManager';



const getCookieStatusBadge = (status) => {
  switch (status) {
    case 'active':
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#EBF5ED] text-[#1E7132]"><CheckCircle2 size={12} />Active</span>;
    case 'inactive':
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF9E6] text-[#8F6702]"><AlertCircle size={12} />Inactive</span>;
    case 'banned':
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FCE9EC] text-[#A8112A]"><XCircle size={12} />Banned</span>;
    default:
      return null;
  }
};

const EngagementBadge = ({ level, score }) => {
  let colorClass = '';
  let text = '';
  switch (level) {
    case 'high':
      colorClass = 'bg-emerald-100 text-emerald-700 border-emerald-200';
      text = 'Rất Tốt';
      break;
    case 'medium':
      colorClass = 'bg-yellow-100 text-yellow-700 border-yellow-200';
      text = 'Trung bình';
      break;
    case 'low':
      colorClass = 'bg-orange-100 text-orange-700 border-orange-200';
      text = 'Hơi Kém';
      break;
    case 'very_low':
      colorClass = 'bg-red-100 text-red-700 border-red-200';
      text = 'Kém';
      break;
    default:
      colorClass = 'bg-gray-100 text-gray-700 border-gray-200';
      text = 'Không rõ';
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
      {text} ({Math.round(score)}%)
    </span>
  );
};

const MarketTrendTech = ({ trend }) => {
  if (!trend) return null;

  const { status, avg_score, analyzed_at } = trend;
  const score = Math.min(100, Math.max(0, Math.round(avg_score)));

  const config = {
    'Tốt': {
      color: '#10b981', // emerald-500
      glow: '0 0 20px rgba(16, 185, 129, 0.4)',
      bg: 'rgba(16, 185, 129, 0.1)',
    },
    'Trung bình': {
      color: '#f59e0b', // amber-500
      glow: '0 0 20px rgba(245, 158, 11, 0.4)',
      bg: 'rgba(245, 158, 11, 0.1)',
    },
    'Kém': {
      color: '#ef4444', // red-500
      glow: '0 0 20px rgba(239, 68, 68, 0.4)',
      bg: 'rgba(239, 68, 68, 0.1)',
    }
  };

  const { color, glow, bg } = config[status] || config['Trung bình'];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0f172a]/90 backdrop-blur-2xl p-4 shadow-xl mb-6 group ring-1 ring-white/10 transition-all duration-300">
      <div
        className="absolute -right-8 -top-8 w-32 h-32 rounded-full blur-[60px] opacity-10 transition-all duration-1000 group-hover:opacity-20"
        style={{ backgroundColor: color }}
      />

      <div className="flex items-center gap-6 relative z-10">
        <div className="relative w-20 h-20">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-white/5" />
            <circle
              cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8"
              strokeDasharray={`${(score * 2.64).toFixed(1)} 264`} strokeLinecap="round"
              style={{ filter: `drop-shadow(${glow})` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl font-black text-white">{score}</span>
          </div>
        </div>

        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: color, boxShadow: glow }} />
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">Market Vibe Index</span>
          </div>
          <h2 className="text-xl font-black text-white tracking-tight">
            Tương tác: <span style={{ color }}>{status}</span>
          </h2>
          <p className="text-[10px] text-white/30 font-medium">
            Update: {new Date(analyzed_at).toLocaleTimeString('vi-VN')}
          </p>
        </div>

        <div
          className="px-4 py-2 rounded-xl border flex items-center gap-2"
          style={{ borderColor: `${color}30`, backgroundColor: bg }}
        >
          <Flame size={16} style={{ color }} />
          <span className="text-[10px] font-black text-white uppercase tracking-tighter">Admin Overview</span>
        </div>
      </div>
    </div>
  );
};

export default function Admin() {

  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [cookies, setCookies] = useState([]);
  const [analyzeRequests, setAnalyzeRequests] = useState([]);
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null, processing: false });
  const [approveForm, setApproveForm] = useState({
    engagement_level: 'low',
    likes: 0,
    comments: 0,
    shares: 0,
    followers: 0,
    engagement_score: 0.0,
    engagement_per_post: 0,
    post_frequency: 0,
    content_quality_score: 0,
    page_name: '',
    description: 'Phân tích và phê duyệt bởi Admin',
    ai_summary: 'Hệ thống đã phân tích chi tiết hồ sơ mạng xã hội của bạn dựa trên dữ liệu tương tác thực tế.'
  });
  const [criteria, setCriteria] = useState(null);
  const [adminWatchlist, setAdminWatchlist] = useState([]);
  const [autoSearchSettings, setAutoSearchSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // User edit
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editBalance, setEditBalance] = useState('');
  const [editSubscriptionType, setEditSubscriptionType] = useState('standard');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cancelVipDialogOpen, setCancelVipDialogOpen] = useState(false);
  const [userToCancelVip, setUserToCancelVip] = useState(null);

  // Cookie management
  const [cookieDialogOpen, setCookieDialogOpen] = useState(false);
  const [newCookieName, setNewCookieName] = useState('');
  const [newCookieString, setNewCookieString] = useState('');
  const [addingCookie, setAddingCookie] = useState(false);
  const [checkingAll, setCheckingAll] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [cookieGender, setCookieGender] = useState('both');
  const [checkingSingle, setCheckingSingle] = useState(false);
  const [isCookieLive, setIsCookieLive] = useState(null);

  // Criteria
  const [savingCriteria, setSavingCriteria] = useState(false);

  // Watchlist
  const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
  const [newWatchUrl, setNewWatchUrl] = useState('');
  const [newWatchName, setNewWatchName] = useState('');
  const [newWatchType, setNewWatchType] = useState('page');
  const [addingWatchlist, setAddingWatchlist] = useState(false);

  // Auto search settings
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);

  // AI Settings
  const [aiSettings, setAiSettings] = useState(null);
  const [savingAiSettings, setSavingAiSettings] = useState(false);

  // Character Mapping
  const [charMapping, setCharMapping] = useState(null);
  const [charMappingText, setCharMappingText] = useState('');
  const [savingCharMapping, setSavingCharMapping] = useState(false);

  // Site Settings
  const [siteSettings, setSiteSettings] = useState({
    site_name: '', logo_url: '', zalo_link: '', facebook_link: '', telegram_link: '', hotline: '', about_text: '',
    telegram_bot_token: '', telegram_chat_id: ''
  });
  const [savingSiteSettings, setSavingSiteSettings] = useState(false);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [annDialogOpen, setAnnDialogOpen] = useState(false);
  const [newAnnTitle, setNewAnnTitle] = useState('');
  const [newAnnContent, setNewAnnContent] = useState('');
  const [newAnnIsPopup, setNewAnnIsPopup] = useState(false);
  // Source Pool
  const [sourcePool, setSourcePool] = useState([]);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceGender, setNewSourceGender] = useState('both');
  const [newSourceNote, setNewSourceNote] = useState('');
  const [addingSource, setAddingSource] = useState(false);
  const [importingSource, setImportingSource] = useState(false);
  const [runningPoolScan, setRunningPoolScan] = useState(false);
  const [cleaningPool, setCleaningPool] = useState(false);
  const [checkingPoolStatus, setCheckingPoolStatus] = useState(false);

  const [savingAnn, setSavingAnn] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState([]);
  const [loadingDiscovery, setLoadingDiscovery] = useState(false);
  
  // Maintenance User Stats
  const [userUsageStats, setUserUsageStats] = useState([]);
  const [loadingUserUsage, setLoadingUserUsage] = useState(false);
  const [resetingUser, setResetingUser] = useState(null);

  // Market Trend Settings State
  const [marketTrendUrls, setMarketTrendUrls] = useState('');
  // Edit Latest Market Trend Mode
  const [editTrendScore, setEditTrendScore] = useState('');
  const [editTrendStatus, setEditTrendStatus] = useState('Tốt');
  const [isEditingTrend, setIsEditingTrend] = useState(false);

  // New Market Trend State Variables
  const [marketTrendMinLikes, setMarketTrendMinLikes] = useState(100);
  const [marketTrendMinComments, setMarketTrendMinComments] = useState(10);
  const [marketTrendMinShares, setMarketTrendMinShares] = useState(5);
  const [marketTrendGoodPercent, setMarketTrendGoodPercent] = useState(50);
  const [marketTrendMediumPercent, setMarketTrendMediumPercent] = useState(30);
  const [marketTrendLowPercent, setMarketTrendLowPercent] = useState(10);
  const [marketTrendPostsPerLink, setMarketTrendPostsPerLink] = useState(3);
  const [marketTrendCheckTimes, setMarketTrendCheckTimes] = useState([]);
  const [marketTrendAiEnabled, setMarketTrendAiEnabled] = useState(false);
  const [marketTrendAssessmentMode, setMarketTrendAssessmentMode] = useState('scheduled');

  const [savingMarketTrend, setSavingMarketTrend] = useState(false);
  const [runningMarketTrend, setRunningMarketTrend] = useState(false);
  const [newCheckTime, setNewCheckTime] = useState('');

  // Feature Flags
  const [featureFlags, setFeatureFlags] = useState([]);
  const [savingFeatureFlags, setSavingFeatureFlags] = useState({});

  // Topup State
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [topupUserTarget, setTopupUserTarget] = useState(null);
  const [topupType, setTopupType] = useState('credits');
  const [topupAmount, setTopupAmount] = useState('');
  const [topupExpiryDays, setTopupExpiryDays] = useState(30);
  const [performingTopup, setPerformingTopup] = useState(false);
  const [vipPrice, setVipPrice] = useState(500000);
  const [vipExpiryDays, setVipExpiryDays] = useState(30);
  const [topupStartDate, setTopupStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [savingVipConfig, setSavingVipConfig] = useState(false);
  
  // Password Update State
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUserTarget, setPasswordUserTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  
  // Tutorials
  const [tutorials, setTutorials] = useState([]);
  const [editingTutorial, setEditingTutorial] = useState({ feature_id: 'dashboard', title: '', content: '', is_active: true });
  const [savingTutorial, setSavingTutorial] = useState(false);

  // Maintenance management
  const [maintenanceStats, setMaintenanceStats] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(7);
  const [cleanupCollections, setCleanupCollections] = useState(['analyzed_links', 'notification_logs']);
  const [analyzedLinks, setAnalyzedLinks] = useState([]);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [dailySearchStats, setDailySearchStats] = useState([]);
  const [loadingDailyStats, setLoadingDailyStats] = useState(false);
  const [compensatingUser, setCompensatingUser] = useState(null);

  // System Log Analysis
  const [systemLogs, setSystemLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const tutorialFeatures = [
    { id: 'dashboard', name: 'Tổng quan' },
    { id: 'analyze', name: 'Phân tích & Lịch sử' },
    { id: 'auto-search', name: 'Tự động tìm' },
    { id: 'ai-content', name: 'AI Content' },
    { id: 'bypass-content', name: 'Lách Content' },
    { id: 'hotface', name: 'Hotface List' },
    { id: 'notifications', name: 'Thông báo' },
    { id: 'billing', name: 'Gói VIP' },
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // 1. Fetch CRITICAL data first
      try {
        const statsRes = await adminAPI.getStats();
        setStats(statsRes.data);
      } catch (e) {
        console.error("Critical: Failed to load stats", e);
        toast.error('Không thể tải thống kê tổng quan');
      }

      // 2. Fetch all other data in parallel, safely
      const fetchJobs = [
        { key: 'users', call: adminAPI.getUsers(), setter: (res) => setUsers(res.data || []) },
        { key: 'logs', call: adminAPI.getLogs(), setter: (res) => setLogs(res.data || []) },
        { key: 'cookies', call: adminAPI.getCookies(), setter: (res) => setCookies(res.data || []) },
        { key: 'criteria', call: adminAPI.getCriteria(), setter: (res) => setCriteria(res.data || null) },
        { key: 'watchlist', call: adminAPI.getWatchlist(), setter: (res) => setAdminWatchlist(res.data || []) },
        { key: 'autoSettings', call: adminAPI.getAutoSearchSettings(), setter: (res) => setAutoSearchSettings(res.data || {}) },
        { key: 'aiSettings', call: adminAPI.getAISettings(), setter: (res) => setAiSettings(res.data || {}) },
        { key: 'charMapping', call: adminAPI.exportCharMapping(), setter: (res) => setCharMappingText(res.data.mapping_text || '') },
        { key: 'siteSettings', call: adminAPI.getSiteSettings(), setter: (res) => setSiteSettings(res.data || {}) },
        { key: 'announcements', call: adminAPI.getAnnouncements(), setter: (res) => setAnnouncements(res.data || []) },
        { key: 'analyzeRequests', call: adminAPI.getAnalyzeRequests(), setter: (res) => setAnalyzeRequests(res.data || []) },
        { key: 'pool', call: adminAPI.getSourcePool(), setter: (res) => setSourcePool(res.data || []) },
        { key: 'featureFlags', call: adminAPI.getFeatureFlags(), setter: (res) => setFeatureFlags(res.data || []) },
        { key: 'vipConfig', call: adminAPI.getVipConfig(), setter: (res) => {
            const vcfg = res.data || {};
            setVipPrice(vcfg.price || 500000);
            setVipExpiryDays(vcfg.expiry_days || 30);
          }
        },
        { key: 'tutorials', call: tutorialsAPI.adminGetAll(), setter: (res) => setTutorials(res.data || []) },
      ];

      const results = await Promise.allSettled(fetchJobs.map(job => job.call));
      
      results.forEach((result, index) => {
        const job = fetchJobs[index];
        if (result.status === 'fulfilled') {
          job.setter(result.value);
        } else {
          console.warn(`Non-critical data failed to load: ${job.key}`, result.reason);
        }
      });

      // 3. Load market trend settings (separate try-catch already exists)
      try {
        const mtRes = await adminAPI.getMarketTrendSettings();
        const mt = mtRes.data;
        if (mt.urls) setMarketTrendUrls(mt.urls.join('\n'));
        if (mt.min_likes !== undefined) setMarketTrendMinLikes(mt.min_likes);
        if (mt.min_comments !== undefined) setMarketTrendMinComments(mt.min_comments);
        if (mt.min_shares !== undefined) setMarketTrendMinShares(mt.min_shares);
        if (mt.rating_good_percent !== undefined) setMarketTrendGoodPercent(mt.rating_good_percent);
        if (mt.rating_medium_percent !== undefined) setMarketTrendMediumPercent(mt.rating_medium_percent);
        if (mt.rating_low_percent !== undefined) setMarketTrendLowPercent(mt.rating_low_percent);
        if (mt.posts_per_link !== undefined) setMarketTrendPostsPerLink(mt.posts_per_link);
        if (mt.check_times) setMarketTrendCheckTimes(mt.check_times);
        if (mt.ai_analysis_enabled !== undefined) setMarketTrendAiEnabled(mt.ai_analysis_enabled);
        if (mt.assessment_mode) setMarketTrendAssessmentMode(mt.assessment_mode);

        fetchLatestTrend();
      } catch (e) {
        console.error("Failed to load market trend settings", e);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Không thể tải dữ liệu quản trị');
    } finally {
      setLoading(false);
    }
  };

  // User management
  const [editIsLocked, setEditIsLocked] = useState(false);
  const [editNotificationEmail, setEditNotificationEmail] = useState('');

  // System Log Handlers
  const fetchSystemLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await adminAPI.getSystemLogs();
      setSystemLogs(res.data);
    } catch (error) {
       console.error("Failed to fetch logs:", error);
    } finally {
       setLoadingLogs(false);
    }
  };

  const handleClearSystemLogs = async () => {
    if (!window.confirm("Xác nhận xóa TOÀN BỘ lịch sử thao tác hệ thống? Hành động này không thể hoàn tác.")) return;
    try {
      await adminAPI.clearSystemLogs();
      setSystemLogs([]);
      toast.success("Đã xóa sạch nhật ký hệ thống");
    } catch (error) {
      toast.error("Xóa nhật ký thất bại");
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditRole(user.role);
    setEditBalance(user.balance?.toString() || '0');
    setEditSubscriptionType(user.subscription_type || 'standard');
    setEditIsLocked(!!user.is_locked);
    setEditNotificationEmail(user.notification_email || '');
    setEditDialogOpen(true);
  };
  
  const openPasswordDialog = (user) => {
    setPasswordUserTarget(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };
  
  const handleUpdatePassword = async () => {
    if (!passwordUserTarget || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Mật khẩu phải từ 6 ký tự');
      return;
    }
    setUpdatingPassword(true);
    try {
      await adminAPI.updateUser(passwordUserTarget.id, { password: newPassword });
      toast.success(`Đã đổi mật khẩu cho ${passwordUserTarget.name}`);
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Đổi mật khẩu thất bại');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const updateData = {
        role: editRole,
        subscription_type: editSubscriptionType,
        is_locked: editIsLocked,
        notification_email: editNotificationEmail
      };

      // Ensure VIP users have a valid expiry date
      if (editSubscriptionType === 'monthly') {
        const now = new Date();
        const currentExpiry = editingUser.subscription_expiry ? new Date(editingUser.subscription_expiry) : null;
        if (!currentExpiry || currentExpiry < now) {
          const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
          updateData.subscription_expiry = thirtyDaysLater.toISOString();
        }
      }

      await adminAPI.updateUser(editingUser.id, updateData);
      await fetchData();
      toast.success('Đã cập nhật người dùng!');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLockUser = async (userId, currentLocked) => {
    try {
      await adminAPI.updateUser(userId, { is_locked: !currentLocked });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_locked: !currentLocked } : u));
      toast.success(currentLocked ? 'Đã mở khóa tài khoản' : 'Đã khóa tài khoản');
    } catch (error) {
      toast.error('Thao tác thất bại');
    }
  };

  const handleToggleApproveUser = async (userId, currentApproved) => {
    if (currentApproved) return;
    try {
      await adminAPI.updateUser(userId, { is_approved: true });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_approved: true } : u));
      toast.success('Đã duyệt tài khoản thành công');
    } catch (error) {
      toast.error('Thao tác thất bại');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Xác nhận xóa người dùng "${userName}"? Dữ liệu sẽ bị xóa vĩnh viễn.`)) return;
    try {
      await adminAPI.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Đã xóa người dùng');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Xóa thất bại');
    }
  };

  // Cookie management
  const handleAddCookie = async () => {
    if (!newCookieString) {
      toast.error('Vui lòng nhập cookie');
      return;
    }

    setAddingCookie(true);
    try {
      if (bulkMode) {
        const res = await adminAPI.bulkAddCookies(newCookieName || 'Cookie', newCookieString, cookieGender);
        toast.success(res.data.message);
      } else {
        if (!newCookieName) {
          toast.error('Vui lòng nhập tên cookie');
          setAddingCookie(false);
          return;
        }
        await adminAPI.addCookie(newCookieName, newCookieString);
        toast.success('Đã thêm cookie!');
      }
      await fetchData();
      setCookieDialogOpen(false);
      setNewCookieName('');
      setNewCookieString('');
      setIsCookieLive(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Thêm cookie thất bại');
    } finally {
      setAddingCookie(false);
    }
  };

  const handleCheckSingleCookie = async () => {
    if (!newCookieString) {
      toast.error('Vui lòng nhập cookie để kiểm tra');
      return;
    }
    setCheckingSingle(true);
    try {
      const res = await adminAPI.checkSingleCookie(newCookieString);
      setIsCookieLive(res.data.is_live);
      if (res.data.is_live) {
        toast.success('Cookie còn sống (Live)');
      } else {
        toast.error('Cookie đã chết (Die)');
      }
    } catch (error) {
      toast.error('Lỗi khi kiểm tra cookie');
    } finally {
      setCheckingSingle(false);
    }
  };

  const handleCheckAllCookies = async () => {
    setCheckingAll(true);
    try {
      const res = await adminAPI.checkAllCookies();
      toast.success(res.data.message);
      await fetchData();
    } catch (error) {
      toast.error('Kiểm tra thất bại');
    } finally {
      setCheckingAll(false);
    }
  };

  const handleImportCookieTxt = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setNewCookieString(evt.target.result);
      setBulkMode(true);
      if (!newCookieName) setNewCookieName('Imported');
      toast.success('Đã nhận danh sách cookie từ file');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleDeleteCookie = async (id) => {
    if (!confirm('Bạn có chắc chắn muốn xóa cookie này?')) return;

    try {
      await adminAPI.deleteCookie(id);
      setCookies(prev => prev.filter(c => c.id !== id));
      toast.success('Đã xóa cookie');
    } catch (error) {
      toast.error('Xóa thất bại');
    }
  };

  const handleUpdateCookieStatus = async (id, status) => {
    try {
      await adminAPI.updateCookieStatus(id, status);
      setCookies(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      toast.success(`Đã cập nhật trạng thái: ${status}`);
    } catch (error) {
      toast.error('Cập nhật thất bại');
    }
  };

  // Criteria management
  const handleUpdateCriteria = async () => {
    setSavingCriteria(true);
    try {
      const res = await adminAPI.updateCriteria(criteria);
      setCriteria(res.data);
      toast.success('Đã cập nhật tiêu chí đánh giá!');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingCriteria(false);
    }
  };

  // Admin watchlist management
  const handleAddWatchlist = async () => {
    if (!newWatchUrl || !newWatchName) {
      toast.error('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    setAddingWatchlist(true);
    try {
      await adminAPI.addWatchlist(newWatchUrl, newWatchName, newWatchType);
      await fetchData();
      toast.success('Đã thêm vào danh sách!');
      setWatchlistDialogOpen(false);
      setNewWatchUrl('');
      setNewWatchName('');
    } catch (error) {
      toast.error('Thêm thất bại');
    } finally {
      setAddingWatchlist(false);
    }
  };

  const handleDeleteWatchlist = async (id) => {
    try {
      await adminAPI.deleteWatchlist(id);
      setAdminWatchlist(prev => prev.filter(w => w.id !== id));
      toast.success('Đã xóa');
    } catch (error) {
      toast.error('Xóa thất bại');
    }
  };

  // Source Pool management
  const handleAddSource = async () => {
    if (!newSourceUrl) {
      toast.error('Vui lòng nhập URL');
      return;
    }
    setAddingSource(true);
    try {
      await adminAPI.addSourceItem(newSourceUrl, newSourceGender, newSourceNote);
      await fetchSourcePool();
      toast.success('Đã thêm vào nguồn chung');
      setSourceDialogOpen(false);
      setNewSourceUrl('');
      setNewSourceGender('both');
      setNewSourceNote('');
    } catch (error) {
      toast.error('Thêm thất bại');
    } finally {
      setAddingSource(false);
    }
  };

  const handleDeleteSource = async (id) => {
    try {
      await adminAPI.deleteSourceItem(id);
      setSourcePool(prev => prev.filter(s => s.id !== id));
      toast.success('Đã xóa nguồn');
    } catch (error) {
      toast.error('Xóa thất bại');
    }
  };

  const handleResetPoolUsage = async () => {
    if (!confirm("Bạn có chắc chắn muốn reset trạng thái của tất cả link nguồn? Việc này sẽ giúp tất cả người dùng có thể nhận lại các link cũ trong kho.")) {
      return;
    }
    setCheckingPoolStatus(true); // Re-use loading state
    try {
      const res = await adminAPI.resetSourcePoolUsage();
      toast.success(res.data.message);
      await fetchSourcePool();
    } catch (error) {
      toast.error('Reset thất bại: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCheckingPoolStatus(false);
    }
  };


  const handleImportSourceTxt = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.txt')) {
      toast.error('Vui lòng chọn file .txt');
      return;
    }
    setImportingSource(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const text = evt.target.result;
        const urls = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        if (urls.length === 0) {
          toast.error('File không có nội dung');
          return;
        }

        const res = await adminAPI.importSourcePool(urls);
        toast.success(`Đã import thành công ${res.data.success} link. Bỏ qua ${res.data.duplicates} link trùng.`);

        const poolRes = await adminAPI.getSourcePool();
        setSourcePool(poolRes.data || []);
      } catch (error) {
        toast.error('Import thất bại: ' + (error.response?.data?.detail || error.message));
      } finally {
        setImportingSource(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };
  
  const handleCleanupPool = async () => {
    setCleaningPool(true);
    try {
      const res = await adminAPI.cleanupDuplicates();
      toast.success(res.data.message);
      await fetchSourcePool();
    } catch (error) {
      toast.error('Dọn dẹp thất bại');
    } finally {
      setCleaningPool(false);
    }
  };

  const handleCheckPoolStatus = async () => {
    const toastId = toast.loading('Đang khởi động quét ngầm toàn bộ kho...');
    try {
      const res = await adminAPI.checkSourceStatus();
      toast.success(res.data.message || 'Bot đã bắt đầu quét ngầm! Bạn có thể làm việc khác thoải mái.', { id: toastId, duration: 8000 });
      
      // Auto-refresh pool list every 30s to see the "Updating..." items get real names
      const pollId = setInterval(() => {
        fetchSourcePool();
      }, 30000);

      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(pollId), 5 * 60 * 1000);
    } catch (error) {
      toast.error('Gặp lỗi: ' + (error.response?.data?.detail || error.message), { id: toastId });
    }
  };

  const fetchDailySearchStats = async () => {
    setLoadingDailyStats(true);
    try {
      const statsRes = await adminAPI.getDailySearchStats();
      const statsData = statsRes.data || [];
      
      // Ensure ALL users are in the stats table even if not in the daily results
      const allStats = users.map(u => {
        const userStat = statsData.find(s => s.user_id === u.id);
        return {
          user_id: u.id,
          name: u.name,
          email: u.email,
          discovery: userStat ? userStat.discovery : 0,
          pool: userStat ? userStat.pool : 0,
          total: userStat ? userStat.total : 0
        };
      });
      
      setDailySearchStats(allStats);
    } catch (err) {
      console.error("Failed to fetch daily stats", err);
    } finally {
      setLoadingDailyStats(false);
    }
  };

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  const handleManualCompensate = (userId) => {
    const amount = autoSearchSettings.pool_links_per_user_day || 5;
    setConfirmMessage(`Xác nhận bù đắp thêm ${amount} link từ Kho Ma Thuật cho người dùng này?`);
    setConfirmAction(() => async () => {
      setCompensatingUser(userId);
      try {
        const res = await adminAPI.compensateUser(userId, amount);
        const actualCount = res.data?.count ?? 0;
        const message = res.data?.message || '';
        if (actualCount === 0) {
          toast.warning(message || 'Không có link nào khả dụng trong Kho Ma Thuật để bù đắp.');
        } else {
          toast.success(message || `Đã bù đắp thành công ${actualCount} link!`);
        }
        fetchDailySearchStats();
      } catch (err) {
        toast.error("Bù đắp thất bại: " + (err.response?.data?.detail || err.message));
      } finally {
        setCompensatingUser(null);
      }
    });
    setConfirmDialogOpen(true);
  };

  useEffect(() => {
    if (activeTab === 'auto-settings') {
      fetchDailySearchStats();
    }
  }, [activeTab]);

  // Auto search settings
  const handleUpdateAutoSettings = async (overrideSettings = null) => {
    setSavingAutoSettings(true);
    try {
      const settingsToSave = overrideSettings || autoSearchSettings;
      const res = await adminAPI.updateAutoSearchSettings(settingsToSave);
      setAutoSearchSettings(res.data);
      toast.success('Đã cập nhật cài đặt!');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingAutoSettings(false);
    }
  };

  // AI Settings management
  const handleUpdateAiSettings = async () => {
    setSavingAiSettings(true);
    try {
      const payload = { ...aiSettings };
      if (typeof payload.ai_api_keys === 'string') {
        payload.ai_api_keys = payload.ai_api_keys.split('\n').map(k => k.trim()).filter(k => k !== '');
      }
      if (typeof payload.content_api_keys === 'string') {
        payload.content_api_keys = payload.content_api_keys.split('\n').map(k => k.trim()).filter(k => k !== '');
      }
      if (typeof payload.groq_api_keys === 'string') {
        payload.groq_api_keys = payload.groq_api_keys.split('\n').map(k => k.trim()).filter(k => k !== '');
      }
      if (typeof payload.openrouter_api_keys === 'string') {
        payload.openrouter_api_keys = payload.openrouter_api_keys.split('\n').map(k => k.trim()).filter(k => k !== '');
      }
      if (typeof payload.deepseek_free_api_keys === 'string') {
        payload.deepseek_free_api_keys = payload.deepseek_free_api_keys.split('\n').map(k => k.trim()).filter(k => k !== '');
      }
      const res = await adminAPI.updateAISettings(payload);
      toast.success('Đã cập nhật cài đặt AI!');
      // Reload settings to refresh masked key list
      const refreshed = await adminAPI.getAISettings();
      setAiSettings({ ...refreshed.data, ai_api_keys: '', content_api_keys: '', groq_api_keys: '', openrouter_api_keys: '', deepseek_free_api_keys: '' });
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingAiSettings(false);
    }
  };

  const handleDeleteAiKey = async (index, provider) => {
    if (!window.confirm(`Xóa key #${index + 1} của ${provider}?`)) return;
    try {
      await adminAPI.deleteAIKey(index, provider);
      toast.success(`Đã xóa key #${index + 1}`);
      // Reload settings
      const refreshed = await adminAPI.getAISettings();
      setAiSettings({ ...refreshed.data, ai_api_keys: '' });
    } catch (error) {
      toast.error('Xóa key thất bại');
    }
  };

  const [checkingAiKeys, setCheckingAiKeys] = useState(false);
  const handleResetAiKeyStatus = async () => {
    setCheckingAiKeys(true);
    const tid = toast.loading('Đang gửi test prompt để kiểm tra từng Key...');
    try {
      const res = await adminAPI.resetAISettingsStatus();
      const live = res.data.live_count;
      const total = res.data.total_count;
      
      toast.success(`Xong! Phát hiện ${live}/${total} key hoạt động tốt.`, { id: tid, duration: 5000 });
      
      const refreshed = await adminAPI.getAISettings();
      setAiSettings(prev => ({ ...prev, ai_api_keys_status: refreshed.data.ai_api_keys_status }));
    } catch (error) {
      toast.error('Làm mới trạng thái thất bại', { id: tid });
    } finally {
      setCheckingAiKeys(false);
    }
  };

  // Character Mapping management
  const handleUpdateCharMapping = async () => {
    if (!charMappingText.trim()) {
      toast.error('Vui lòng nhập bảng thay thế ký tự');
      return;
    }
    setSavingCharMapping(true);
    try {
      await adminAPI.updateCharMapping(charMappingText);
      toast.success('Đã cập nhật bảng thay thế ký tự!');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingCharMapping(false);
    }
  };

  // Tutorial management
  const handleSaveTutorial = async () => {
    if (!editingTutorial.feature_id || !editingTutorial.title || !editingTutorial.content) {
      toast.error('Vui lòng điền đầy đủ thông tin');
      return;
    }
    setSavingTutorial(true);
    try {
      await tutorialsAPI.adminSave(editingTutorial);
      toast.success('Đã lưu hướng dẫn!');
      const res = await tutorialsAPI.adminGetAll();
      setTutorials(res.data);
      setEditingTutorial({ feature_id: 'dashboard', title: '', content: '', is_active: true });
    } catch (error) {
      toast.error('Lưu hướng dẫn thất bại');
    } finally {
      setSavingTutorial(false);
    }
  };

  const handleDeleteTutorial = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa hướng dẫn này?')) return;
    try {
      await tutorialsAPI.adminDelete(id);
      setTutorials(prev => prev.filter(t => t.id !== id));
      toast.success('Đã xóa hướng dẫn');
    } catch (error) {
      toast.error('Xóa thất bại');
    }
  };

  const loadTutorialToEdit = (tutorial) => {
    setEditingTutorial({
      feature_id: tutorial.feature_id,
      title: tutorial.title,
      content: tutorial.content,
      is_active: tutorial.is_active
    });
    // Scroll to top of edit form
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const handleImportTxt = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.txt')) {
      toast.error('Vui lòng chọn file .txt');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCharMappingText(evt.target.result);
      toast.success(`Đã import file ${file.name}`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleExportTxt = () => {
    const blob = new Blob([charMappingText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bypass_rules.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Site Settings handlers
  const handleSaveSiteSettings = async () => {
    setSavingSiteSettings(true);
    try {
      const res = await adminAPI.updateSiteSettings(siteSettings);
      setSiteSettings(res.data);
      toast.success('Đã cập nhật cài đặt trang!');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingSiteSettings(false);
    }
  };

  // Announcement handlers
  const handleCreateAnnouncement = async () => {
    if (!newAnnTitle.trim() || !newAnnContent.trim()) {
      toast.error('Vui lòng nhập tiêu đề và nội dung');
      return;
    }
    setSavingAnn(true);
    try {
      await adminAPI.createAnnouncement({ 
        title: newAnnTitle, 
        content: newAnnContent, 
        is_active: true,
        is_popup: newAnnIsPopup
      });
      setAnnDialogOpen(false);
      setNewAnnTitle('');
      setNewAnnContent('');
      setNewAnnIsPopup(false);
      toast.success('Đã tạo thông báo!');
      const res = await adminAPI.getAnnouncements();
      setAnnouncements(res.data || []);
    } catch (error) {
      toast.error('Tạo thông báo thất bại');
    } finally {
      setSavingAnn(false);
    }
  };

  const handleToggleAnnouncement = async (id, currentActive) => {
    try {
      await adminAPI.updateAnnouncement(id, { is_active: !currentActive });
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, is_active: !currentActive } : a));
      toast.success(currentActive ? 'Đã tắt thông báo' : 'Đã bật thông báo');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    }
  };

  const handleTogglePopupAnnouncement = async (id, currentIsPopup) => {
    try {
      await adminAPI.updateAnnouncement(id, { is_popup: !currentIsPopup });
      const res = await adminAPI.getAnnouncements();
      setAnnouncements(res.data || []);
      toast.success(!currentIsPopup ? 'Đã bật thông báo nổi bật' : 'Đã tắt thông báo nổi bật');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await adminAPI.deleteAnnouncement(id);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast.success('Đã xóa thông báo');
    } catch (error) {
      toast.error('Xóa thất bại');
    }
  };

  const handleToggleFeature = async (flagId, currentEnabled) => {
    setSavingFeatureFlags(prev => ({ ...prev, [flagId]: true }));
    try {
      await adminAPI.updateFeatureFlag(flagId, { is_enabled: !currentEnabled });
      setFeatureFlags(prev => prev.map(f => f.id === flagId ? { ...f, is_enabled: !currentEnabled } : f));
      toast.success(`${currentEnabled ? 'Đã tắt' : 'Đã bật'} tính năng thành công`);
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingFeatureFlags(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const handleUpdateFeatureMessage = async (flagId, message) => {
    setSavingFeatureFlags(prev => ({ ...prev, [flagId]: true }));
    try {
      await adminAPI.updateFeatureFlag(flagId, { message });
      setFeatureFlags(prev => prev.map(f => f.id === flagId ? { ...f, message } : f));
      toast.success('Đã cập nhật thông báo bảo trì');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingFeatureFlags(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const handleUpdateFeatureCost = async (flagId, creditCost) => {
    setSavingFeatureFlags(prev => ({ ...prev, [flagId]: true }));
    try {
      await adminAPI.updateFeatureFlag(flagId, { credit_cost: parseInt(creditCost) || 0 });
      setFeatureFlags(prev => prev.map(f => f.id === flagId ? { ...f, credit_cost: parseInt(creditCost) || 0 } : f));
      toast.success('Đã cập nhật chi phí credits');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingFeatureFlags(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const handleToggleVIPFeature = async (flagId, currentVIP) => {
    setSavingFeatureFlags(prev => ({ ...prev, [flagId]: true }));
    try {
      const isNowVIP = !currentVIP;
      const updateData = { is_vip: isNowVIP };
      
      await adminAPI.updateFeatureFlag(flagId, updateData);
      setFeatureFlags(prev => prev.map(f => f.id === flagId ? { ...f, ...updateData } : f));
      toast.success(`Đã chuyển sang chế độ ${isNowVIP ? 'VIP (Gói tháng)' : 'Credits'}`);
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingFeatureFlags(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const handleUpdateFeatureVipMessage = async (flagId, vipMessage) => {
    setSavingFeatureFlags(prev => ({ ...prev, [flagId]: true }));
    try {
      await adminAPI.updateFeatureFlag(flagId, { vip_message: vipMessage });
      setFeatureFlags(prev => prev.map(f => f.id === flagId ? { ...f, vip_message: vipMessage } : f));
      toast.success('Đã cập nhật thông báo VIP');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingFeatureFlags(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const openTopupDialog = (user) => {
    setTopupUserTarget(user);
    setTopupAmount('');
    setTopupType('monthly');
    setTopupDialogOpen(true);
  };

  const handleTopup = async () => {
    if (!topupUserTarget) return;
    setPerformingTopup(true);
    try {
      await adminAPI.topupUser(topupUserTarget.id, {
        type: topupType,
        amount: topupType === 'credits' ? parseFloat(topupAmount) : null,
        subscription_start: topupType === 'monthly' ? topupStartDate : null,
        expiry_days: topupType === 'monthly' ? parseInt(topupExpiryDays) : null
      });
      toast.success('Nạp tiền/Gói thành công!');
      setTopupDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Nạp tiền thất bại');
    } finally {
      setPerformingTopup(false);
    }
  };

  const fetchLatestTrend = async () => {
    try {
      const res = await adminAPI.getLatestMarketTrend();
      if (res.data) {
        setEditTrendScore(res.data.avg_score);
        setEditTrendStatus(res.data.status);
      }
    } catch (error) {
      console.error("Failed to fetch latest trend", error);
    }
  };

  const handleSaveMarketTrend = async () => {
    setSavingMarketTrend(true);
    try {
      const urls = marketTrendUrls.split('\n').map(u => u.trim()).filter(u => u.length > 0);
      await adminAPI.updateMarketTrendSettings({
        urls,
        min_likes: parseInt(marketTrendMinLikes),
        min_comments: parseInt(marketTrendMinComments),
        min_shares: parseInt(marketTrendMinShares),
        rating_good_percent: parseFloat(marketTrendGoodPercent),
        rating_medium_percent: parseFloat(marketTrendMediumPercent),
        rating_low_percent: parseFloat(marketTrendLowPercent),
        posts_per_link: parseInt(marketTrendPostsPerLink),
        check_times: marketTrendCheckTimes,
        ai_analysis_enabled: marketTrendAiEnabled,
        assessment_mode: marketTrendAssessmentMode
      });
      toast.success('Đã lưu cấu hình. Hệ thống sẽ tự động bắt đầu đánh giá mới.');
      // Auto trigger assessment
      handleRunMarketTrend();
    } catch (error) {
      toast.error('Lưu cấu hình thất bại');
    } finally {
      setSavingMarketTrend(false);
    }
  };

  const handleSaveVipConfig = async () => {
    setSavingVipConfig(true);
    try {
      await adminAPI.updateVipConfig({ price: parseFloat(vipPrice), expiry_days: parseInt(vipExpiryDays) });
      toast.success('Đã cập nhật cấu hình VIP!');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingVipConfig(false);
    }
  };

  const handleRunMarketTrend = async () => {
    setRunningMarketTrend(true);
    try {
      const res = await adminAPI.runMarketTrend();
      toast.success(`Đã đánh giá xong: ${res.data.status} (${res.data.avg_score} điểm)`);
      fetchLatestTrend(); // Refresh the edit fields with new result
    } catch (error) {
      toast.error('Đánh giá thất bại: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRunningMarketTrend(false);
    }
  };
  
  const handleCancelVipClick = (userId) => {
    setUserToCancelVip(userId);
    setCancelVipDialogOpen(true);
  };

  const confirmCancelVip = async () => {
    if (!userToCancelVip) return;
    setSaving(true);
    try {
      await adminAPI.cancelVip(userToCancelVip);
      setUsers(prev => prev.map(u => u.id === userToCancelVip ? { ...u, subscription_type: 'regular', subscription_expiry: null } : u));
      toast.success('Đã hủy gói VIP thành công');
      setCancelVipDialogOpen(false);
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Hủy gói VIP thất bại');
    } finally {
      setSaving(false);
      setUserToCancelVip(null);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );



  const fetchMaintenanceStats = async () => {
    try {
      const [statsRes, linksRes, usageRes] = await Promise.all([
        adminAPI.getMaintenanceStats(),
        adminAPI.getAnalyzedLinks(),
        adminAPI.getUserUsageStats()
      ]);
      setMaintenanceStats(statsRes.data.collection_stats);
      setAnalyzedLinks(linksRes.data || []);
      setUserUsageStats(usageRes.data || []);
    } catch (error) {
      console.error("Failed to fetch maintenance stats", error);
    }
  };

  const handleResetUser = async (userId, collections) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa sạch dữ liệu của người dùng này?')) return;
    setResetingUser(userId);
    try {
      await adminAPI.resetUserData(userId, collections);
      toast.success('Đã reset dữ liệu người dùng thành công');
      fetchMaintenanceStats();
    } catch (err) {
      toast.error('Reset thất bại');
    } finally {
      setResetingUser(null);
    }
  };

  const triggerCleanup = () => {
    if (cleanupCollections.length === 0) {
      toast.error("Vui lòng chọn ít nhất 1 bảng dữ liệu để dọn dẹp!");
      return;
    }
    setCleanupConfirmOpen(true);
  };

  const handleCleanup = async () => {
    setCleanupConfirmOpen(false);
    setCleaningUp(true);
    try {
      const res = await adminAPI.cleanupData({
        collections: cleanupCollections,
        older_than_days: cleanupDays
      });
      
      let msg = "Dọn dẹp thành công!";
      Object.entries(res.data.deleted_counts).forEach(([col, count]) => {
        msg += `\n- ${col}: đã xóa ${count} bản ghi`;
      });
      
      toast.success(msg);
      fetchMaintenanceStats(); // Refresh stats
      adminAPI.getStats().then(sRes => setStats(sRes.data)); // Refresh overview stats
    } catch (error) {
      toast.error("Dọn dẹp thất bại: " + (error.response?.data?.detail || error.message));
    } finally {
      setCleaningUp(false);
    }
  };

  const fetchSourcePool = async () => {
    try {
      const res = await adminAPI.getSourcePool();
      setSourcePool(res.data || []);
    } catch (err) {
      console.error("Failed to fetch source pool", err);
    }
  };

  const handleRunPoolScan = async () => {
    try {
      setRunningPoolScan(true);
      const res = await adminAPI.runDiscoveryScan({
        keywords: autoSearchSettings.discovery_keywords || "hot face, fashion, booking kols",
        max_results: 20,
        save_to_pool: true
      });
      const addedCount = res.data?.results?.length || 0;
      toast.success(`Đã quét xong bài viết hot. Đã lọc và thêm được ${addedCount} link vào Pool chung.`);
      await fetchSourcePool();
    } catch (err) {
      toast.error("Lỗi khi quét Pool: " + (err.response?.data?.detail || err.message));
    } finally {
      setRunningPoolScan(false);
    }
  };



  const fetchDiscoveryResults = async () => {
    try {
      setLoadingDiscovery(true);
      const res = await adminAPI.getDiscoveryResults();
      setDiscoveryResults(res.data || []);
    } catch (err) {
      console.error("Failed to fetch discovery results", err);
    } finally {
      setLoadingDiscovery(false);
    }
  };

  const handleDeleteDiscovery = async (id) => {
    try {
      await adminAPI.deleteDiscoveryResult(id);
      setDiscoveryResults(prev => prev.filter(r => r.id !== id));
      toast.success('Đã xóa bản ghi!');
    } catch (err) {
      toast.error('Xóa thất bại');
    }
  };

  const handleApproveToPool = async (result) => {
    try {
      await adminAPI.addSourceItem(result.url, result.gender, `Duyệt từ Kho Tìm Hot: ${result.page_name}`);
      toast.success(`Đã thêm ${result.page_name} vào Nguồn chung!`);
      // Optionally remove from discovery or mark as moved
    } catch (err) {
      toast.error('Thêm vào nguồn chung thất bại');
    }
  };

  const handleApproveAnalyzeRequest = async () => {
    if (!approveDialog.request) return;
    setApproveDialog(prev => ({ ...prev, processing: true }));
    try {
      await adminAPI.approveAnalyzeRequest(approveDialog.request.id, approveForm);
      toast.success('Đã duyệt yêu cầu phân tích thành công!');
      setApproveDialog({ open: false, request: null, processing: false });
      const res = await adminAPI.getAnalyzeRequests();
      setAnalyzeRequests(res.data || []);
    } catch (error) {
      toast.error('Lỗi khi duyệt yêu cầu');
      setApproveDialog(prev => ({ ...prev, processing: false }));
    }
  };

  const handleRejectAnalyzeRequest = async (id) => {
    if (!window.confirm('Từ chối yêu cầu này và hoàn tiền cho user?')) return;
    try {
      await adminAPI.rejectAnalyzeRequest(id);
      toast.success('Đã từ chối và hoàn tiền');
      const res = await adminAPI.getAnalyzeRequests();
      setAnalyzeRequests(res.data || []);
    } catch (error) {
      toast.error('Lỗi khi từ chối yêu cầu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
          <Shield className="text-[#1877F2]" />
          Quản trị hệ thống
        </h1>
        <p className="text-[#606770] mt-1">
          Quản lý người dùng, cookies và tiêu chí đánh giá
        </p>
      </div>

      {/* Market Trend Tech Section */}
      <MarketTrendTech trend={stats?.market_trend} />

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="stagger-2 transition-all duration-300">
        {/* Mobile Tab Selected */}
        <div className="md:hidden mb-6 flex items-center justify-between gap-3 bg-[#F0F2F5] p-1.5 rounded-xl border border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-[#1877F2] shadow-sm shrink-0">
            <Settings2 size={20} />
          </div>
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="flex-1 bg-white border-none shadow-sm h-10 font-semibold text-[#1C1E21] focus:ring-0">
              <SelectValue placeholder="Chọn danh mục quản trị" />
            </SelectTrigger>
            <SelectContent align="end" className="max-h-[300px]">
              <SelectItem value="overview">Tổng quan</SelectItem>
              <SelectItem value="users">Người dùng</SelectItem>
              <SelectItem value="cookies">Cookies FB</SelectItem>
              <SelectItem value="criteria">Tiêu chí</SelectItem>
              <SelectItem value="auto-settings">Tự động tìm</SelectItem>
              <SelectItem value="source-pool">Nguồn chung</SelectItem>
              <SelectItem value="ai-keys">AI Keys</SelectItem>
              <SelectItem value="bypass-rules">Lách content</SelectItem>
              <SelectItem value="logs">Nhật ký (Logs)</SelectItem>
              <SelectItem value="site-settings">Cấu hình Site</SelectItem>
              <SelectItem value="announcements">Thông báo hệ thống</SelectItem>
              <SelectItem value="features">Feature Flags</SelectItem>
              <SelectItem value="market-trend">FB Trend</SelectItem>
              <SelectItem value="vip-config">Cấu hình VIP</SelectItem>
              <SelectItem value="guides">Hướng dẫn hệ thống</SelectItem>
              <SelectItem value="maintenance">Bảo trì dữ liệu</SelectItem>
              <SelectItem value="discovery-results">Kho Tìm Hot</SelectItem>
              <SelectItem value="livestream">Phát Livestream</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Tabs */}
        <TabsList className="hidden md:flex bg-[#F0F2F5] p-1 mb-6 w-full flex-wrap h-auto justify-start gap-2">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white" data-testid="overview-tab">
            <LayoutDashboard size={16} className="mr-2" />
            Tổng quan
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white" data-testid="users-tab">
            <Users size={16} className="mr-2" />
            Người dùng
          </TabsTrigger>
          <TabsTrigger value="cookies" className="data-[state=active]:bg-white" data-testid="cookies-tab">
            <Cookie size={16} className="mr-2" />
            Cookies FB
          </TabsTrigger>
          <TabsTrigger value="analyze-requests" className="data-[state=active]:bg-white">
            <Search size={16} className="mr-2" />
            Yêu cầu Check Hot
          </TabsTrigger>
          <TabsTrigger value="criteria" className="data-[state=active]:bg-white" data-testid="criteria-tab">
            <Settings2 size={16} className="mr-2" />
            Tiêu chí
          </TabsTrigger>
          <TabsTrigger value="auto-settings" className="data-[state=active]:bg-white" data-testid="auto-settings-tab">
            <Zap size={16} className="mr-2" />
            Zap
          </TabsTrigger>
          <TabsTrigger value="source-pool" className="data-[state=active]:bg-white" data-testid="source-pool-tab">
            <Zap size={16} className="mr-2 text-[#F5C33B]" />
            Nguồn
          </TabsTrigger>
          <TabsTrigger value="ai-keys" className="data-[state=active]:bg-white" data-testid="ai-keys-tab">
            <Key size={16} className="mr-2" />
            AI Keys
          </TabsTrigger>
          <TabsTrigger value="bypass-rules" className="data-[state=active]:bg-white" data-testid="bypass-rules-tab">
            <Sparkles size={16} className="mr-2" />
            Lách content
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-white" data-testid="logs-tab">
            <FileText size={16} className="mr-2" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="site-settings" className="data-[state=active]:bg-white" data-testid="site-settings-tab">
            <Image size={16} className="mr-2" />
            Site
          </TabsTrigger>
          <TabsTrigger value="announcements" className="data-[state=active]:bg-white" data-testid="announcements-tab">
            <Megaphone size={16} className="mr-2" />
            TBáo
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-white" data-testid="features-tab">
            <Shield size={16} className="mr-2 text-[#1877F2]" />
            F-Flags
          </TabsTrigger>
          <TabsTrigger value="market-trend" className="data-[state=active]:bg-white" data-testid="market-trend-tab">
            <Flame size={16} className="mr-2 text-[#E74C3C]" />
            FB-Trend
          </TabsTrigger>
          <TabsTrigger value="vip-config" className="data-[state=active]:bg-white" data-testid="vip-config-tab">
            <Crown size={16} className="mr-2 text-amber-500" />
            VIP-Config
          </TabsTrigger>
          <TabsTrigger value="guides" className="data-[state=active]:bg-white" data-testid="tutorials-tab">
            <BookOpen size={16} className="mr-2 text-[#31A24C]" />
            Hướng dẫn
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-white" data-testid="maintenance-tab" onClick={fetchMaintenanceStats}>
            <Trash2 size={16} className="mr-2 text-red-500" />
            Bảo trì
          </TabsTrigger>
          <TabsTrigger value="discovery-results" className="data-[state=active]:bg-white" data-testid="discovery-tab" onClick={fetchDiscoveryResults}>
            <FileSearch size={16} className="mr-2 text-indigo-500" />
            Kho Tìm Hot
          </TabsTrigger>
          <TabsTrigger value="system-logs" className="data-[state=active]:bg-white" data-testid="system-logs-tab" onClick={fetchSystemLogs}>
            <History size={16} className="mr-2 text-gray-600" />
            Nhật ký
          </TabsTrigger>
          <TabsTrigger value="livestream" className="data-[state=active]:bg-white" data-testid="livestream-tab">
            <Video size={16} className="mr-2 text-red-500" />
            Livestream
          </TabsTrigger>
        </TabsList>

        {/* --- 1. OVERVIEW TAB --- */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
            <div className="p-6 bg-white rounded-2xl border shadow-sm flex items-center gap-4 transition-all hover:shadow-md hover:border-[#1877F2]/30 group">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-[#1877F2] group-hover:bg-[#1877F2] group-hover:text-white transition-colors">
                <Users size={24} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#1C1E21]">{stats?.total_users || 0}</div>
                <div className="text-sm text-[#606770] font-medium">Tổng người dùng</div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-2xl border shadow-sm flex items-center gap-4 transition-all hover:shadow-md hover:border-amber-400/30 group">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-white transition-colors">
                <Crown size={24} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#1C1E21]">{stats?.total_vip || 0}</div>
                <div className="text-sm text-[#606770] font-medium">Thành viên VIP</div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-2xl border shadow-sm flex items-center gap-4 transition-all hover:shadow-md hover:border-emerald-500/30 group">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                <Zap size={24} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#1C1E21]">{stats?.usage_today || 0}</div>
                <div className="text-sm text-[#606770] font-medium">Lượt phân tích link (24h)</div>
              </div>
            </div>

            <div 
              className="p-6 bg-white rounded-2xl border shadow-sm flex items-center gap-4 transition-all hover:shadow-md hover:border-orange-500/30 group cursor-pointer"
              onClick={() => setActiveTab('source-pool')}
            >
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <Flame size={24} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#1C1E21]">{stats?.total_pool_links || 0}</div>
                <div className="text-sm text-[#606770] font-medium">Nguồn chung</div>
              </div>
            </div>

            <div 
              className="p-6 bg-white rounded-2xl border shadow-sm flex items-center gap-4 transition-all hover:shadow-md hover:border-indigo-500/30 group cursor-pointer"
              onClick={() => setActiveTab('discovery-results')}
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                <FileSearch size={24} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#1C1E21]">{stats?.discovery_count || 0}</div>
                <div className="text-sm text-[#606770] font-medium">Phát hiện mới (Discovery)</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <UserPlus size={18} className="text-[#1877F2]" />
                  Người dùng mới gần đây
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.recent_users?.map(u => (
                    <div key={u.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#F0F2F5] flex items-center justify-center text-[#606770] font-bold">
                          {u.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-[#1C1E21]">{u.name}</p>
                          <p className="text-xs text-[#606770]">{u.email}</p>
                        </div>
                      </div>
                      <div className="text-right text-xs">
                        <p className="font-medium text-[#606770]">{new Date(u.created_at).toLocaleDateString('vi-VN')}</p>
                        <p className="text-[#8C939D]">{u.role === 'admin' ? 'Admin' : 'User'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <History size={18} className="text-[#1877F2]" />
                  Nhật ký thông báo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {logs.slice(0, 8).map((log, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#1C1E21] line-clamp-2">{log.message}</p>
                        <p className="text-[10px] text-[#8C939D] mt-0.5">{new Date(log.sent_at).toLocaleString('vi-VN')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* --- TUTORIALS TAB --- */}
        <TabsContent value="guides" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Edit3 size={18} className="text-[#31A24C]" />
                  {editingTutorial.feature_id ? 'Chỉnh sửa hướng dẫn' : 'Thêm hướng dẫn mới'}
                </CardTitle>
                <CardDescription>Soạn thảo nội dung hướng dẫn cho từng tính năng</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tính năng</Label>
                  <Select 
                    value={editingTutorial.feature_id} 
                    onValueChange={(val) => setEditingTutorial(prev => ({...prev, feature_id: val}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tính năng..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tutorialFeatures.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tiêu đề hướng dẫn</Label>
                  <Input 
                    placeholder="VD: Cách phân tích link Facebook hiệu quả"
                    value={editingTutorial.title}
                    onChange={(e) => setEditingTutorial(prev => ({...prev, title: e.target.value}))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Nội dung (Hỗ trợ Markdown cơ bản)</Label>
                  <Textarea 
                    placeholder="Nhập nội dung chi tiết ở đây..."
                    rows={12}
                    className="font-mono text-sm"
                    value={editingTutorial.content}
                    onChange={(e) => setEditingTutorial(prev => ({...prev, content: e.target.value}))}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch 
                    id="tutorial-active" 
                    checked={editingTutorial.is_active}
                    onCheckedChange={(val) => setEditingTutorial(prev => ({...prev, is_active: val}))}
                  />
                  <Label htmlFor="tutorial-active">Hiển thị cho người dùng</Label>
                </div>

                <div className="pt-2 flex gap-2">
                  <Button 
                    className="flex-1 bg-[#31A24C] hover:bg-[#2D9144]"
                    onClick={handleSaveTutorial}
                    disabled={savingTutorial}
                  >
                    {savingTutorial ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                    Lưu hướng dẫn
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setEditingTutorial({ feature_id: 'dashboard', title: '', content: '', is_active: true })}
                  >
                    Hủy
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <BookOpen size={18} className="text-[#31A24C]" />
                  Danh sách hướng dẫn hiện có
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tutorials.length > 0 ? tutorials.map((t) => (
                    <div key={t.id} className="p-4 bg-[#F7F8FA] rounded-xl border border-transparent hover:border-gray-200 transition-all flex flex-col md:flex-row justify-between md:items-center gap-4">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-[#1C1E21] truncate">{t.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {tutorialFeatures.find(f => f.id === t.feature_id)?.name || t.feature_id}
                          </Badge>
                          {!t.is_active && <Badge variant="destructive" className="text-[10px] uppercase">Ẩn</Badge>}
                          <span className="text-[10px] text-[#8C939D]">Cập nhật: {new Date(t.updated_at).toLocaleDateString('vi-VN')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => loadTutorialToEdit(t)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteTutorial(t.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-[#8C939D]">
                      <Info size={32} className="mx-auto mb-2 opacity-20" />
                      <p className="text-sm">Chưa có bài hướng dẫn nào.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>


        {/* Cookies Tab */}
        <TabsContent value="cookies" className="mt-6">
          <Card data-testid="cookies-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold">Quản lý Cookie Facebook</CardTitle>
                  <CardDescription>
                    Thêm cookie từ tài khoản Facebook để hệ thống có thể crawl dữ liệu
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={handleCheckAllCookies}
                    disabled={checkingAll || cookies.length === 0}
                    className="border-[#1877F2] text-[#1877F2] hover:bg-[#E7F3FF]"
                  >
                    {checkingAll ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
                    Check All Live
                  </Button>
                  <label htmlFor="import-cookie-txt">
                    <input
                      id="import-cookie-txt"
                      type="file"
                      accept=".txt"
                      onChange={handleImportCookieTxt}
                      className="hidden"
                    />
                    <Button variant="outline" asChild>
                      <span className="cursor-pointer flex items-center">
                        <Upload size={16} className="mr-2" />
                        Import .txt
                      </span>
                    </Button>
                  </label>
                  <Button
                    onClick={() => {
                      setBulkMode(false);
                      setCookieDialogOpen(true);
                    }}
                    className="bg-[#1877F2] hover:bg-[#166FE5]"
                    data-testid="add-cookie-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    Thêm Cookie
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {cookies.length > 0 ? (
                <div className="space-y-3">
                  {cookies.map((cookie) => (
                    <div
                      key={cookie.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-[#F7F8FA] rounded-xl gap-4"
                      data-testid={`cookie-${cookie.id}`}
                    >
                      <div className="flex items-center gap-4 w-full md:w-auto">
                        <div className="w-10 h-10 rounded-full bg-[#E7F3FF] flex items-center justify-center shrink-0">
                          <Cookie size={20} className="text-[#1877F2]" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-[#1C1E21] truncate">{cookie.name}</h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#606770] mt-1">
                            <span>Dùng: {cookie.use_count} lần</span>
                            {cookie.last_used && (
                              <span>Lần cuối: {new Date(cookie.last_used).toLocaleDateString('vi-VN')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 w-full md:w-auto mt-2 md:mt-0 justify-end md:justify-start">
                        {getCookieStatusBadge(cookie.status)}
                        <Select
                          value={cookie.status}
                          onValueChange={(value) => handleUpdateCookieStatus(cookie.id, value)}
                        >
                          <SelectTrigger className="w-32" data-testid={`status-select-${cookie.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="banned">Banned</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#E41E3F] hover:text-[#E41E3F] hover:bg-[#FCE9EC]"
                          onClick={() => handleDeleteCookie(cookie.id)}
                          data-testid={`delete-cookie-${cookie.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Cookie size={48} className="empty-state-icon mx-auto" />
                  <h3 className="empty-state-title">Chưa có cookie nào</h3>
                  <p className="empty-state-text">
                    Thêm cookie Facebook để hệ thống có thể crawl dữ liệu tương tác
                  </p>
                  <Button
                    onClick={() => setCookieDialogOpen(true)}
                    className="bg-[#1877F2] hover:bg-[#166FE5]"
                  >
                    <Plus size={16} className="mr-2" />
                    Thêm Cookie đầu tiên
                  </Button>
                </div>
              )}

              <div className="mt-6 p-4 bg-[#E7F3FF] rounded-xl">
                <h4 className="font-semibold text-[#1877F2] mb-2">Hướng dẫn lấy Cookie Facebook:</h4>
                <ol className="text-sm text-[#606770] space-y-1 list-decimal list-inside">
                  <li>Đăng nhập Facebook trên trình duyệt</li>
                  <li>Nhấn F12 → Tab "Application" → Cookies → facebook.com</li>
                  <li>Copy tất cả cookie hoặc dùng extension "EditThisCookie"</li>
                  <li>Paste vào đây dạng: c_user=xxx; xs=xxx; datr=xxx...</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Criteria Tab */}
        <TabsContent value="criteria" className="mt-6">
          <Card data-testid="criteria-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Tiêu chí đánh giá tương tác</CardTitle>
              <CardDescription>
                Cấu hình ngưỡng để đánh giá mức độ tương tác của một bài viết/profile Facebook
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {criteria && autoSearchSettings && (
                <>
                  {/* --- SECTION 1: DISCOVERY CRITERIA (AUTO SEARCH) --- */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                        <Zap size={18} />
                      </div>
                      <h3 className="font-bold text-lg text-[#1C1E21]">Tiêu chí Khám phá (Phát hiện bài viết hot)</h3>
                    </div>
                    <p className="text-sm text-[#606770]">Cấu hình ngưỡng để hệ thống tự động lọc ra các bài viết chất lượng trên Facebook trước khi quét profile.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Likes */}
                      <div className={`p-4 border rounded-xl space-y-3 bg-white transition-opacity ${!autoSearchSettings.min_post_likes_enabled ? 'opacity-50' : ''}`}>
                         <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold flex items-center gap-2">
                               <ThumbsUp size={14} className="text-[#1877F2]" /> Likes ≥
                            </Label>
                            <Switch 
                               checked={autoSearchSettings.min_post_likes_enabled ?? true}
                               onCheckedChange={(checked) => setAutoSearchSettings(prev => ({ ...prev, min_post_likes_enabled: checked }))}
                            />
                         </div>
                         <Input 
                            type="number"
                            value={autoSearchSettings.min_post_likes ?? 500}
                            onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, min_post_likes: parseInt(e.target.value) || 0 }))}
                            disabled={!autoSearchSettings.min_post_likes_enabled}
                         />
                      </div>

                      {/* Comments */}
                      <div className={`p-4 border rounded-xl space-y-3 bg-white transition-opacity ${!autoSearchSettings.min_post_comments_enabled ? 'opacity-50' : ''}`}>
                         <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold flex items-center gap-2">
                               <MessageCircle size={14} className="text-[#1877F2]" /> Comments ≥
                            </Label>
                            <Switch 
                               checked={autoSearchSettings.min_post_comments_enabled ?? true}
                               onCheckedChange={(checked) => setAutoSearchSettings(prev => ({ ...prev, min_post_comments_enabled: checked }))}
                            />
                         </div>
                         <Input 
                            type="number"
                            value={autoSearchSettings.min_post_comments ?? 50}
                            onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, min_post_comments: parseInt(e.target.value) || 0 }))}
                            disabled={!autoSearchSettings.min_post_comments_enabled}
                         />
                      </div>

                      {/* Shares */}
                      <div className={`p-4 border rounded-xl space-y-3 bg-white transition-opacity ${!autoSearchSettings.min_post_shares_enabled ? 'opacity-50' : ''}`}>
                         <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold flex items-center gap-2">
                               <Share2 size={14} className="text-[#1877F2]" /> Shares ≥
                            </Label>
                            <Switch 
                               checked={autoSearchSettings.min_post_shares_enabled ?? true}
                               onCheckedChange={(checked) => setAutoSearchSettings(prev => ({ ...prev, min_post_shares_enabled: checked }))}
                            />
                         </div>
                         <Input 
                            type="number"
                            value={autoSearchSettings.min_post_shares ?? 20}
                            onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, min_post_shares: parseInt(e.target.value) || 0 }))}
                            disabled={!autoSearchSettings.min_post_shares_enabled}
                         />
                      </div>
                    </div>
                    <div className="p-3 bg-orange-50 text-orange-700 rounded-lg text-xs italic">
                        * Những tiêu chí này giúp hệ thống quyết định bài viết nào đủ "nóng" để truy cập vào trang cá nhân tác giả.
                    </div>
                    <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={handleUpdateAutoSettings}
                        disabled={savingAutoSettings}
                        className="w-full md:w-auto"
                    >
                        {savingAutoSettings ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                        Lưu tiêu chí khám phá
                    </Button>
                  </div>

                  {/* --- SECTION 2: EVALUATION CRITERIA (PROFILE ANALYSIS) --- */}
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#1877F2] flex items-center justify-center">
                        <Users size={18} />
                      </div>
                      <h3 className="font-bold text-lg text-[#1C1E21]">Tiêu chí Đánh giá Profile (Thông tin Hotface)</h3>
                    </div>
                    <p className="text-sm text-[#606770]">Cấu hình tiêu chuẩn để đánh giá một Profile có phải là Hotface tiềm năng hay không.</p>

                    <div className="space-y-4">
                        {/* Followers */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-xl bg-white gap-4">
                            <div className="flex items-center gap-3">
                                <Switch
                                    checked={criteria.followers_enabled}
                                    onCheckedChange={(checked) => setCriteria(prev => ({ ...prev, followers_enabled: checked }))}
                                />
                                <div>
                                    <Label className="font-bold">Số người theo dõi (Followers)</Label>
                                    <p className="text-xs text-[#606770]">Xác định quy mô cộng đồng của Hotface</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-[#606770]">Tối thiểu:</span>
                                <Input
                                    type="number"
                                    value={criteria.min_followers}
                                    onChange={(e) => setCriteria(prev => ({ ...prev, min_followers: parseInt(e.target.value) || 0 }))}
                                    className="w-32 font-bold"
                                    disabled={!criteria.followers_enabled}
                                />
                            </div>
                        </div>

                        {/* Reels Views (NEW) */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-xl bg-white gap-4 border-purple-200 shadow-sm shadow-purple-50 transition-all hover:bg-purple-50/20">
                            <div className="flex items-center gap-3">
                                <Switch
                                    checked={criteria?.reels_views_enabled || false}
                                    onCheckedChange={(checked) => setCriteria(prev => ({ ...prev, reels_views_enabled: checked }))}
                                />
                                <div>
                                    <Label className="font-bold flex items-center gap-2">
                                        <Bot size={18} className="text-purple-600" />
                                        Tiêu chí lượt xem Reels (Views)
                                    </Label>
                                    <p className="text-xs text-[#606770]">Hệ thống sẽ vào /reels để tính trung bình lượt xem từ 5-10 video mới nhất</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-[#606770]">Ngưỡng tối thiểu:</span>
                                <Input
                                    type="number"
                                    value={criteria?.min_reels_views ?? 2000}
                                    onChange={(e) => setCriteria(prev => ({ ...prev, min_reels_views: parseInt(e.target.value) || 0 }))}
                                    className="w-32 font-bold text-purple-600 h-9"
                                    disabled={!criteria?.reels_views_enabled}
                                />
                            </div>
                        </div>

                        {/* Scan Depth (NEW) */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-xl bg-white gap-4 border-[#1877F2]/20 shadow-sm shadow-[#1877F2]/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#1877F2]">
                                    <FileSearch size={20} />
                                </div>
                                <div>
                                    <Label className="font-bold">Độ sâu quét (Scan Depth)</Label>
                                    <p className="text-xs text-[#606770]">Số lượng bài viết mới nhất trên Profile để phân tích tương tác</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-[#606770]">Số bài viết:</span>
                                <Input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={criteria.posts_per_profile || 10}
                                    onChange={(e) => setCriteria(prev => ({ ...prev, posts_per_profile: parseInt(e.target.value) || 10 }))}
                                    className="w-32 font-bold text-[#1877F2]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Engagement Thresholds */}
                    <div className="p-4 bg-[#F7F8FA] rounded-xl space-y-4 border">
                        <h4 className="font-bold text-sm text-[#1C1E21] flex items-center gap-2">
                            <Flame size={16} className="text-orange-500" /> Ngưỡng phân loại tương tác
                        </h4>
                        <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-xl mb-6">
                      <div className="space-y-1">
                        <Label className="text-orange-900 font-bold">Chế độ Thuật toán Dự phòng (Fallback)</Label>
                        <p className="text-xs text-orange-700">Nếu tất cả AI Key hết lượt hoặc lỗi, hệ thống sẽ tự cào dữ liệu DOM và đánh giá theo thuật toán thay vì báo lỗi.</p>
                      </div>
                        <Switch
                          checked={criteria.enable_ai_fallback_algorithm || false}
                          onCheckedChange={(checked) => setCriteria(prev => ({ ...prev, enable_ai_fallback_algorithm: checked }))}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase text-[#606770]">Rất Tốt (High) khi đạt ≥</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={criteria.high_threshold_percent}
                                        onChange={(e) => setCriteria(prev => ({ ...prev, high_threshold_percent: parseInt(e.target.value) || 0 }))}
                                        className="w-24 border-green-200"
                                    />
                                    <span className="text-sm font-medium">% chỉ số</span>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase text-[#606770]">Trung bình (Medium) khi đạt ≥</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={criteria.medium_threshold_percent}
                                        onChange={(e) => setCriteria(prev => ({ ...prev, medium_threshold_percent: parseInt(e.target.value) || 0 }))}
                                        className="w-24 border-yellow-200"
                                    />
                                    <span className="text-sm font-medium">% chỉ số</span>
                                </div>
                            </div>
                        </div>
                        <p className="text-[10px] text-[#8C939D] leading-relaxed">
                            * Công thức: Dựa trên Profile có đạt đủ các tiêu chí tối thiểu về Followers và độ tương tác bài viết hay không.
                        </p>
                    </div>

                    <Button
                        onClick={handleUpdateCriteria}
                        className="w-full bg-[#1877F2] hover:bg-[#166FE5] shadow-lg shadow-blue-100 h-12 text-base font-bold"
                        disabled={savingCriteria}
                    >
                        {savingCriteria ? (
                            <><Loader2 size={18} className="mr-2 animate-spin" /> Đang lưu tiêu chí...</>
                        ) : (
                            <><Save size={18} className="mr-2" /> Lưu tiêu chí đánh giá Profile</>
                        )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Keys Tab */}
        <TabsContent value="ai-keys" className="mt-6">
          <Card data-testid="ai-keys-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Cài đặt AI API Key</CardTitle>
              <CardDescription>
                Cấu hình API key cho AI content generator
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {aiSettings && (
                <>
                  <div className="p-4 border rounded-xl space-y-4">
                    <div className="space-y-2">
                      <Label>Provider</Label>
                      <Select
                        value={aiSettings.ai_provider || 'gemini'}
                        onValueChange={(value) => setAiSettings(prev => ({ ...prev, ai_provider: value }))}
                      >
                        <SelectTrigger data-testid="ai-provider-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gemini">Google Gemini (Quét bài & Chung)</SelectItem>
                          <SelectItem value="content">Google Gemini (Chỉ dành cho Viết Content)</SelectItem>
                          <SelectItem value="groq">Groq (Nhanh & Miễn phí)</SelectItem>
                          <SelectItem value="openrouter">OpenRouter</SelectItem>
                          <SelectItem value="deepseek_free">DeepSeek Free (ds2api)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Dựa vào provider đang chọn để quyết định biến nào được dùng */}
                    {(() => {
                      const currentProvider = aiSettings.ai_provider || 'gemini';
                      
                      let currentMaskedKeys = [];
                      let currentKeyInput = '';
                      let currentModel = '';
                      let setKeyInput = () => {};
                      let setModelInput = () => {};
                      
                      if (currentProvider === 'gemini') {
                        currentMaskedKeys = aiSettings.ai_api_keys_masked || [];
                        currentKeyInput = Array.isArray(aiSettings.ai_api_keys) ? aiSettings.ai_api_keys.join('\n') : (aiSettings.ai_api_keys || '');
                        currentModel = aiSettings.ai_model || 'gemini-2.0-flash';
                        setKeyInput = (val) => setAiSettings(prev => ({ ...prev, ai_api_keys: val }));
                        setModelInput = (val) => setAiSettings(prev => ({ ...prev, ai_model: val }));
                      } else if (currentProvider === 'content') {
                        currentMaskedKeys = aiSettings.content_api_keys_masked || [];
                        currentKeyInput = Array.isArray(aiSettings.content_api_keys) ? aiSettings.content_api_keys.join('\n') : (aiSettings.content_api_keys || '');
                        currentModel = 'gemini-1.5-flash (Fixed)';
                        setKeyInput = (val) => setAiSettings(prev => ({ ...prev, content_api_keys: val }));
                        setModelInput = () => {};
                      } else if (currentProvider === 'groq') {
                        currentMaskedKeys = aiSettings.groq_api_keys_masked || [];
                        currentKeyInput = Array.isArray(aiSettings.groq_api_keys) ? aiSettings.groq_api_keys.join('\n') : (aiSettings.groq_api_keys || '');
                        currentModel = aiSettings.groq_model || 'llama-3.1-8b-instant';
                        setKeyInput = (val) => setAiSettings(prev => ({ ...prev, groq_api_keys: val }));
                        setModelInput = (val) => setAiSettings(prev => ({ ...prev, groq_model: val }));
                      } else if (currentProvider === 'openrouter') {
                        currentMaskedKeys = aiSettings.openrouter_api_keys_masked || [];
                        currentKeyInput = Array.isArray(aiSettings.openrouter_api_keys) ? aiSettings.openrouter_api_keys.join('\n') : (aiSettings.openrouter_api_keys || '');
                        currentModel = aiSettings.openrouter_model || 'meta-llama/llama-3-8b-instruct:free';
                        setKeyInput = (val) => setAiSettings(prev => ({ ...prev, openrouter_api_keys: val }));
                        setModelInput = (val) => setAiSettings(prev => ({ ...prev, openrouter_model: val }));
                      } else if (currentProvider === 'deepseek_free') {
                        currentMaskedKeys = aiSettings.deepseek_free_api_keys_masked || [];
                        currentKeyInput = Array.isArray(aiSettings.deepseek_free_api_keys) ? aiSettings.deepseek_free_api_keys.join('\n') : (aiSettings.deepseek_free_api_keys || '');
                        currentModel = aiSettings.deepseek_free_model || 'deepseek-chat';
                        setKeyInput = (val) => setAiSettings(prev => ({ ...prev, deepseek_free_api_keys: val }));
                        setModelInput = (val) => setAiSettings(prev => ({ ...prev, deepseek_free_model: val }));
                      }

                      return (
                        <>
                          <div className="space-y-2">
                            <Label>Model</Label>
                            <Input
                              value={currentModel}
                              onChange={(e) => setModelInput(e.target.value)}
                              placeholder={currentProvider === 'gemini' ? 'gemini-2.0-flash' : currentProvider === 'groq' ? 'llama-3.1-8b-instant' : currentProvider === 'deepseek_free' ? 'deepseek-chat' : 'meta-llama/llama-3-8b-instruct:free'}
                            />
                          </div>

                          {currentProvider === 'deepseek_free' && (
                            <div className="space-y-2">
                              <Label>Base URL (ds2api Endpoint)</Label>
                              <Input
                                value={aiSettings.deepseek_free_base_url || ''}
                                onChange={(e) => setAiSettings(prev => ({ ...prev, deepseek_free_base_url: e.target.value }))}
                                placeholder="http://your-ds2api-url:8000/v1"
                              />
                              <p className="text-[10px] text-blue-600 font-medium italic">
                                * Ví dụ: http://localhost:8000/v1 hoặc https://ds2api.yourdomain.com/v1
                              </p>
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label>API Keys đang lưu ({currentProvider.toUpperCase()})</Label>
                            {currentMaskedKeys.length > 0 ? (
                              <div className="space-y-2">
                                {currentMaskedKeys.map((maskedKey, idx) => (
                                  <div key={idx} className="flex flex-col md:flex-row justify-between md:items-center gap-2 px-3 py-2 bg-[#F0F2F5] rounded-lg border border-[#E4E6EB]">
                                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 flex-1">
                                      <div className="flex items-center gap-2 min-w-[140px]">
                                        <span className="text-xs text-[#8C939D]">#{idx + 1}</span>
                                        <code className="text-sm font-mono text-[#1C1E21]">{maskedKey}</code>
                                      </div>
                                      {/* Trạng thái chỉ hiển thị chi tiết cho gemini (vì hệ thống hiện tại mới scan gemini detailed) */}
                                      {currentProvider === 'gemini' && aiSettings.ai_api_keys_status?.[idx] && (
                                        <div className="flex items-center gap-1.5 overflow-hidden">
                                          <div className={`w-2 h-2 rounded-full shrink-0 ${
                                            aiSettings.ai_api_keys_status[idx].status === 'active' ? 'bg-green-500' : 
                                            aiSettings.ai_api_keys_status[idx].status === 'exhausted' ? 'bg-red-500' : 
                                            aiSettings.ai_api_keys_status[idx].status === 'safety_block' ? 'bg-orange-500' : 'bg-yellow-500'
                                          }`} />
                                          <span className={`text-[10px] font-bold uppercase shrink-0 ${
                                            aiSettings.ai_api_keys_status[idx].status === 'active' ? 'text-green-600' : 
                                            aiSettings.ai_api_keys_status[idx].status === 'exhausted' ? 'text-red-600' : 
                                            aiSettings.ai_api_keys_status[idx].status === 'safety_block' ? 'text-orange-600' : 'text-yellow-600'
                                          }`}>
                                            {aiSettings.ai_api_keys_status[idx].status === 'active' ? 'Hoạt động' : 
                                            aiSettings.ai_api_keys_status[idx].status === 'exhausted' ? 'Hết lượt' : 
                                            aiSettings.ai_api_keys_status[idx].status === 'safety_block' ? 'Chặn an toàn' : 'Lỗi'}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleDeleteAiKey(idx, currentProvider)}
                                      className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
                                      title="Xóa key này"
                                    >
                                      ✕ Xóa
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-[#8C939D] italic">Chưa có key nào được lưu cho {currentProvider.toUpperCase()}.</p>
                            )}

                            <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <Label>Thêm key mới ({currentProvider.toUpperCase()}) - Mỗi dòng 1 key</Label>
                              {currentProvider === 'gemini' && (
                                <button 
                                  onClick={handleResetAiKeyStatus}
                                  disabled={checkingAiKeys}
                                  className={`text-[#1877F2] hover:underline text-xs flex items-center gap-1 ${checkingAiKeys ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <RefreshCw size={12} className={checkingAiKeys ? 'animate-spin' : ''} /> 
                                  {checkingAiKeys ? 'Đang kiểm tra...' : 'Check live & làm mới trạng thái'}
                                </button>
                              )}
                            </div>
                            <Textarea
                              value={currentKeyInput}
                              onChange={(e) => setKeyInput(e.target.value)}
                              placeholder={`Dán API key ${currentProvider} mới vào đây, mỗi dòng 1 key...`}
                              className="min-h-[80px] font-mono text-sm"
                            />
                            <p className="text-xs text-[#8C939D]">
                              Key mới sẽ được THÊM VÀO danh sách khi bạn bấm Lưu. Hệ thống tự động chuyển sang key tiếp theo khi key hiện tại bị lỗi/hết quota.
                            </p>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                    <div className="p-4 bg-purple-50 rounded-xl">
                      <h4 className="font-semibold text-purple-700 mb-2">Hướng dẫn lấy OpenRouter API Key:</h4>
                      <ol className="text-sm text-purple-800 space-y-1 list-decimal list-inside">
                        <li>Truy cập <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="text-purple-700 underline font-semibold">OpenRouter Keys</a></li>
                        <li>Tạo tài khoản / Đăng nhập</li>
                        <li>Tạo API Key mới và copy vào ô trên</li>
                        <li>Model miễn phí tốt nhất: "meta-llama/llama-3-8b-instruct:free"</li>
                      </ol>
                    </div>
                  ) : aiSettings.ai_provider === 'deepseek_free' ? (
                    <div className="p-4 bg-slate-100 rounded-xl border border-slate-300">
                      <h4 className="font-semibold text-slate-700 mb-2">Hướng dẫn sử dụng DeepSeek Free (ds2api):</h4>
                      <ol className="text-sm text-slate-800 space-y-1 list-decimal list-inside">
                        <li>Bạn cần chạy repo <a href="https://github.com/CJackHwang/ds2api" target="_blank" rel="noreferrer" className="text-blue-600 underline font-semibold">ds2api</a> (trên Vercel hoặc server riêng)</li>
                        <li>Nhập <strong>Base URL</strong> là địa chỉ server ds2api của bạn (ví dụ: http://localhost:8000/v1)</li>
                        <li>Nhập <strong>Auth Token</strong> (nếu có cấu hình trong ds2api) vào phần API Keys</li>
                        <li>Model mặc định: <code>deepseek-chat</code> hoặc <code>deepseek-reasoner</code></li>
                      </ol>
                    </div>
                  ) : (
                    <div className="p-4 bg-[#E7F3FF] rounded-xl">
                      <h4 className="font-semibold text-[#1877F2] mb-2">Hướng dẫn lấy Gemini API Key (miễn phí):</h4>
                      <ol className="text-sm text-[#606770] space-y-1 list-decimal list-inside">
                        <li>Truy cập <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="text-[#1877F2] underline">Google AI Studio</a></li>
                        <li>Đăng nhập bằng tài khoản Google</li>
                        <li>Click "Create API Key" để tạo key mới</li>
                        <li>Copy key và dán vào ô API Key ở trên</li>
                        <li>Chọn Provider: "Google Gemini", Model: "gemini-2.0-flash"</li>
                      </ol>
                    </div>
                  )}

                  <Button
                    onClick={handleUpdateAiSettings}
                    className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                    disabled={savingAiSettings}
                    data-testid="save-ai-settings-btn"
                  >
                    {savingAiSettings ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" />Đang lưu...</>
                    ) : (
                      <><Save size={16} className="mr-2" />Lưu cài đặt AI</>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bypass Rules Tab */}
        <TabsContent value="bypass-rules" className="mt-6">
          <Card data-testid="bypass-rules-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold">Bảng thay thế ký tự (Lách chính sách)</CardTitle>
                  <CardDescription>
                    Import file .txt hoặc chỉnh sửa trực tiếp bảng thay thế ký tự Unicode
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <label htmlFor="import-txt">
                    <input
                      id="import-txt"
                      type="file"
                      accept=".txt"
                      onChange={handleImportTxt}
                      className="hidden"
                      data-testid="import-txt-input"
                    />
                    <Button
                      variant="outline"
                      asChild
                      data-testid="import-txt-btn"
                    >
                      <span>
                        <Upload size={16} className="mr-2" />
                        Import .txt
                      </span>
                    </Button>
                  </label>
                  <Button
                    variant="outline"
                    onClick={handleExportTxt}
                    data-testid="export-txt-btn"
                  >
                    <Download size={16} className="mr-2" />
                    Export .txt
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nội dung bảng thay thế</Label>
                <Textarea
                  value={charMappingText}
                  onChange={(e) => setCharMappingText(e.target.value)}
                  rows={14}
                  className="font-mono text-sm"
                  placeholder={'a: "а",\nb: "b",\nc: "с",\n...'}
                  data-testid="char-mapping-textarea"
                />
                <p className="text-xs text-[#8C939D]">
                  Định dạng: ký_tự_gốc: "ký_tự_thay_thế" (mỗi dòng hoặc phân cách bằng dấu phẩy)
                </p>
              </div>

              <div className="p-4 bg-[#FEF9E6] border border-[#FFE69C] rounded-xl">
                <h4 className="font-semibold text-[#8F6702] mb-2">Cách hoạt động:</h4>
                <ul className="text-sm text-[#606770] space-y-1 list-disc list-inside">
                  <li>Mỗi ký tự Latin sẽ được thay bằng ký tự Unicode trông tương tự</li>
                  <li>Ví dụ: a → а (Cyrillic), e → е (Cyrillic), o → о (Cyrillic)</li>
                  <li>Mắt người không phân biệt được, nhưng hệ thống AI sẽ không nhận diện từ vi phạm</li>
                  <li>Người dùng bật "Lách chính sách" khi tạo content để áp dụng</li>
                </ul>
              </div>

              <Button
                onClick={handleUpdateCharMapping}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingCharMapping}
                data-testid="save-char-mapping-btn"
              >
                {savingCharMapping ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" />Đang lưu...</>
                ) : (
                  <><Save size={16} className="mr-2" />Lưu bảng thay thế</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card data-testid="users-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold">Danh sách người dùng</CardTitle>
                  <CardDescription>Quản lý tài khoản và quyền truy cập</CardDescription>
                </div>
                <div className="relative w-full sm:w-64 flex-shrink-0">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C939D]" />
                  <Input
                    placeholder="Tìm kiếm..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="user-search-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Người dùng</th>
                      <th>Email</th>
                      <th>Vai trò</th>
                      <th>Trạng thái</th>
                      <th>Lượt dùng (Tháng/Ngày)</th>
                      <th>Gói VIP</th>
                      <th>Ngày đăng ký</th>
                      <th>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.is_locked ? 'bg-[#FCE9EC]' : 'bg-[#E7F3FF]'}`}>
                              <User size={16} className={user.is_locked ? 'text-[#E41E3F]' : 'text-[#1877F2]'} />
                            </div>
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </td>
                        <td className="text-[#606770]">{user.email}</td>
                        <td>
                          {user.role === 'admin' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#E7F3FF] text-[#1877F2]">
                              <Shield size={12} />Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#F0F2F5] text-[#606770]">User</span>
                          )}
                        </td>
                        <td>
                          {user.is_locked ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FCE9EC] text-[#E41E3F]">
                              <Lock size={12} />Khóa
                            </span>
                          ) : !user.is_approved && user.role !== 'admin' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF9E6] text-[#8F6702]">
                              <AlertCircle size={12} />Chờ duyệt
                            </span>
                          ) : (
                            <>
                              {user.status === 'online' ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#EBF5ED] text-[#31A24C]">
                                  <div className="w-1.5 h-1.5 rounded-full bg-[#31A24C] animate-pulse" />Online
                                </span>
                              ) : user.status === 'away' ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF9E6] text-[#8F6702]">
                                  <Clock size={12} />Away
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#F0F2F5] text-[#606770]">
                                  Offline
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100">
                              M: {user.usage_month || 0}
                            </span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-50 text-gray-700 border border-gray-100">
                              D: {user.usage_today || 0}
                            </span>
                          </div>
                        </td>
                        <td>
                          {user.subscription_type === 'monthly' && user.subscription_expiry && new Date(user.subscription_expiry) > new Date() ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase">
                              <Crown size={10} /> VIP
                            </span>
                          ) : (
                            <span className="text-xs text-[#606770]">Thường</span>
                          )}
                        </td>
                        <td className="text-[#8C939D] text-sm">{new Date(user.created_at).toLocaleDateString('vi-VN')}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="outline" onClick={() => openTopupDialog(user)} className="h-8 w-8 bg-[#E7F3FF] text-[#1877F2] border-[#1877F2]/20 hover:bg-[#D8E9FF]">
                                    <Crown size={14} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Gia hạn Gói VIP</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="outline" onClick={() => openEditDialog(user)} className="h-8 w-8">
                                    <Edit2 size={14} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Sửa vai trò</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="outline" onClick={() => openPasswordDialog(user)} className="h-8 w-8 text-amber-600 hover:text-amber-700">
                                    <Key size={14} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Đổi mật khẩu</TooltipContent>
                              </Tooltip>

                              {user.email !== currentUser?.email && (
                                <>
                                  {!user.is_approved && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="icon"
                                          variant="outline"
                                          onClick={() => handleToggleApproveUser(user.id, !!user.is_approved)}
                                          className="h-8 w-8 text-[#31A24C] border-[#31A24C]/20 hover:bg-[#EBF5ED]"
                                        >
                                          <CheckCircle2 size={14} />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Duyệt tài khoản</TooltipContent>
                                    </Tooltip>
                                  )}

                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        onClick={() => handleToggleLockUser(user.id, !!user.is_locked)}
                                        className={`h-8 w-8 ${user.is_locked ? 'text-[#F5C33B] hover:bg-[#FEF9E6]' : 'text-[#31A24C] hover:bg-[#EBF5ED]'}`}
                                      >
                                        {user.is_locked ? <Lock size={14} className="fill-current" /> : <Unlock size={14} />}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>{user.is_locked ? 'Mở khóa' : 'Khóa'}</TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        onClick={() => handleDeleteUser(user.id, user.name)}
                                        className="h-8 w-8 text-[#E41E3F] hover:bg-[#FCE9EC]"
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Xóa vĩnh viễn</TooltipContent>
                                  </Tooltip>
                                </>
                              )}
                            </TooltipProvider>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="mt-6">
          <Card data-testid="logs-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Logs thông báo</CardTitle>
              <CardDescription>Theo dõi tất cả thông báo đã gửi</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length > 0 ? (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-start gap-4 p-4 bg-[#F7F8FA] rounded-xl" data-testid={`log-${log.id}`}>
                      <div className="w-10 h-10 rounded-full bg-[#E7F3FF] flex items-center justify-center flex-shrink-0">
                        <Bell size={18} className="text-[#1877F2]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1C1E21]">{log.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-[#606770]">
                          <span>User: {log.user_id.substring(0, 8)}...</span>
                          <span>Kênh: {log.channel}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${log.status === 'sent' ? 'bg-[#EBF5ED] text-[#1E7132]' : 'bg-[#FCE9EC] text-[#A8112A]'}`}>
                            {log.status === 'sent' ? 'Đã gửi' : 'Thất bại'}
                          </span>
                        </div>
                        <p className="text-xs text-[#8C939D] mt-1">{new Date(log.sent_at).toLocaleString('vi-VN')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FileText size={48} className="empty-state-icon mx-auto" />
                  <h3 className="empty-state-title">Chưa có logs</h3>
                  <p className="empty-state-text">Logs hoạt động sẽ hiển thị ở đây</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Watchlist Tab */}
        <TabsContent value="watchlist" className="mt-6">
          <Card data-testid="admin-watchlist-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold">Danh sách Pages/Groups chung</CardTitle>
                  <CardDescription>
                    Tất cả users sẽ thấy và quét các trang này khi tự động tìm Hotface
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setWatchlistDialogOpen(true)}
                  className="bg-[#1877F2] hover:bg-[#166FE5]"
                  data-testid="add-admin-watchlist-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Thêm mới
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {adminWatchlist.length > 0 ? (
                <div className="space-y-3">
                  {adminWatchlist.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-[#F7F8FA] rounded-xl gap-4"
                      data-testid={`admin-watchlist-${item.id}`}
                    >
                      <div className="flex items-center gap-4 w-full md:w-auto">
                        <div className="w-10 h-10 rounded-full bg-[#E7F3FF] flex items-center justify-center shrink-0">
                          <Globe size={20} className="text-[#1877F2]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1C1E21]">{item.name}</h3>
                          <p className="text-sm text-[#606770]">{item.url}</p>
                          <span className="text-xs text-[#8C939D]">{item.type === 'group' ? 'Group' : 'Fanpage'}</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[#E41E3F] hover:text-[#E41E3F] hover:bg-[#FCE9EC]"
                        onClick={() => handleDeleteWatchlist(item.id)}
                        data-testid={`delete-admin-watchlist-${item.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Globe size={48} className="empty-state-icon mx-auto" />
                  <h3 className="empty-state-title">Chưa có trang nào</h3>
                  <p className="empty-state-text">
                    Thêm các Pages/Groups để tất cả users có thể quét
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto Search Settings Tab */}
        <TabsContent value="auto-settings" className="mt-6">
          <Card data-testid="auto-settings-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Giới hạn tự động tìm Hotface</CardTitle>
              <CardDescription>
                Cấu hình giới hạn để tránh spam cho mỗi user
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {autoSearchSettings && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* General Limit */}
                    <div className="p-4 border rounded-xl space-y-4 bg-white">
                      <div className="flex items-center gap-4">
                        <Zap size={24} className="text-[#F5C33B]" />
                        <div>
                          <Label className="text-base font-semibold">Kết quả / Lần chạy</Label>
                          <p className="text-[10px] text-[#606770]">Tổng số Hotface tối đa trả về mỗi lần quét.</p>
                        </div>
                      </div>
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={autoSearchSettings.max_results_per_run}
                        onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, max_results_per_run: parseInt(e.target.value) || 10 }))}
                        className="h-10"
                        data-testid="max-results-input"
                      />
                    </div>

                    {/* Daily Run Limit */}
                    <div className="p-4 border rounded-xl space-y-4 bg-white">
                      <div className="flex items-center gap-4">
                        <Users size={24} className="text-[#1877F2]" />
                        <div>
                          <Label className="text-base font-semibold">Giới hạn / Ngày</Label>
                          <p className="text-[10px] text-[#606770]">Số lần mỗi user có thể chạy tự động trong 24h.</p>
                        </div>
                      </div>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={autoSearchSettings.max_runs_per_day}
                        onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, max_runs_per_day: parseInt(e.target.value) || 3 }))}
                        className="h-10"
                        data-testid="max-runs-input"
                      />
                    </div>

                    {/* Admin Source Fallback */}
                    <div className="p-4 border rounded-xl space-y-4 bg-white md:col-span-2">
                       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <Globe size={24} className="text-[#1877F2]" />
                          <div>
                            <Label className="text-base font-semibold">Nguồn Admin (Fallback)</Label>
                            <p className="text-[10px] text-[#606770]">Tự động lấy thêm link từ Nguồn chung nếu tìm kiếm không đủ số lượng.</p>
                          </div>
                        </div>
                        <Switch
                          checked={autoSearchSettings.admin_source_enabled ?? true}
                          onCheckedChange={(checked) => setAutoSearchSettings(prev => ({ ...prev, admin_source_enabled: checked }))}
                        />
                      </div>
                      
                      {autoSearchSettings.admin_source_enabled && (
                        <div className="pt-2 border-t border-dashed mt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                           <Label className="text-xs font-medium text-[#606770] whitespace-nowrap">Giới hạn link từ Nguồn chung mỗi lần quét:</Label>
                           <Input
                            type="number"
                            min="1"
                            max="50"
                            value={autoSearchSettings.admin_source_return_count ?? 5}
                            onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, admin_source_return_count: parseInt(e.target.value) || 5 }))}
                            className="h-9 w-24"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Phân đoạn cấu hình Kho Ma Thuật & Hiệu năng */}
                  <div className="p-4 border rounded-xl space-y-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <Label className="text-base font-bold flex items-center gap-2 text-[#1C1E21]">
                      <Database size={18} className="text-amber-500" />
                      Hiệu năng & Bù đắp (Kho Ma Thuật)
                    </Label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Link bù đắp mặc định / ngày</Label>
                        <Input
                          type="number"
                          value={autoSearchSettings.pool_links_per_user_day || 5}
                          onChange={(e) => setAutoSearchSettings({...autoSearchSettings, pool_links_per_user_day: parseInt(e.target.value) || 0})}
                          className="h-10"
                        />
                        <p className="text-[10px] text-gray-500">Giới hạn bù đắp tự động cho mỗi User.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Số từ khóa tối đa / lần chạy</Label>
                        <Input
                          type="number"
                          value={autoSearchSettings.max_keywords_per_run || 5}
                          onChange={(e) => setAutoSearchSettings({...autoSearchSettings, max_keywords_per_run: parseInt(e.target.value) || 0})}
                          className="h-10"
                        />
                        <p className="text-[10px] text-gray-500">Tránh quét quá nhiều từ khóa gây treo bot.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Timeout mỗi từ khóa (giây)</Label>
                        <Input
                          type="number"
                          value={autoSearchSettings.search_timeout_seconds || 120}
                          onChange={(e) => setAutoSearchSettings({...autoSearchSettings, search_timeout_seconds: parseInt(e.target.value) || 0})}
                          className="h-10"
                        />
                        <p className="text-[10px] text-gray-500">Dừng từ khóa nếu tìm kiếm quá lâu (mặc định 120s).</p>
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={handleUpdateAutoSettings} 
                    className="w-full bg-[#1877F2] hover:bg-[#166FE5] shadow-lg h-11 text-base font-semibold"
                    disabled={savingAutoSettings}
                  >
                    {savingAutoSettings ? <Loader2 className="animate-spin mr-2" /> : <Save className="mr-2" />}
                    Lưu tất cả cấu hình Zap
                  </Button>
                
                <hr className="my-8" />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-[#1C1E21]">Thống kê link hôm nay</h3>
                      <p className="text-sm text-[#606770]">Theo dõi hiệu suất tìm kiếm của từng người dùng</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchDailySearchStats} disabled={loadingDailyStats}>
                      <RefreshCw size={14} className={`mr-2 ${loadingDailyStats ? 'animate-spin' : ''}`} /> Làm mới
                    </Button>
                  </div>

                  <div className="border rounded-2xl overflow-hidden bg-white shadow-sm">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-[#F0F2F5] text-[#606770] font-semibold">
                        <tr>
                          <th className="p-4">Người dùng</th>
                          <th className="p-4 text-center">Tìm thấy (Discovery)</th>
                          <th className="p-4 text-center">Bù đắp (Pool)</th>
                          <th className="p-4 text-center">Tổng cộng</th>
                          <th className="p-4 text-right">Thao tác</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {dailySearchStats.length > 0 ? dailySearchStats.map(stat => (
                          <tr key={stat.user_id} className="hover:bg-gray-50 transition-colors">
                            <td className="p-4">
                              <div className="font-semibold text-[#1C1E21]">{stat.name}</div>
                              <div className="text-[10px] text-[#8C939D]">{stat.email}</div>
                            </td>
                            <td className="p-4 text-center font-medium text-emerald-600">+{stat.discovery}</td>
                            <td className="p-4 text-center font-medium text-amber-600">+{stat.pool}</td>
                            <td className="p-4 text-center font-bold text-[#1877F2]">{stat.total}</td>
                            <td className="p-4 text-right">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-8 text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
                                onClick={() => handleManualCompensate(stat.user_id)}
                                disabled={compensatingUser === stat.user_id}
                              >
                                {compensatingUser === stat.user_id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} className="mr-1" />}
                                Bù đắp ngay
                              </Button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" className="p-12 text-center text-gray-400 italic">Hôm nay chưa có dữ liệu tìm kiếm</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                  <div className="p-4 border rounded-xl space-y-4">
                    <div className="flex items-center gap-4">
                      <Search size={24} className="text-[#1877F2]" />
                      <div>
                        <Label className="text-base font-semibold">Từ khóa tìm kiếm mặc định</Label>
                        <p className="text-sm text-[#606770]">Các từ khóa hệ thống sẽ ưu tiên tìm kiếm</p>
                      </div>
                    </div>
                    <Input
                      placeholder="fashion, kols, booking..."
                      value={autoSearchSettings.discovery_keywords || ''}
                      onChange={(e) => setAutoSearchSettings(prev => ({ ...prev, discovery_keywords: e.target.value }))}
                      data-testid="keywords-input"
                    />
                  </div>
                  <div className="p-4 border rounded-xl space-y-4 bg-[#F0F2F5]/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Zap size={24} className="text-amber-500" />
                        <div>
                          <Label className="text-base font-semibold">Chất lượng link phân phối</Label>
                          <p className="text-sm text-[#606770]">Chỉ trả về link đạt mức tương tác này trở lên</p>
                        </div>
                      </div>
                      <Select 
                        value={autoSearchSettings.min_engagement_level || 'medium'} 
                        onValueChange={(val) => setAutoSearchSettings(prev => ({ ...prev, min_engagement_level: val }))}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Thấp (+)</SelectItem>
                          <SelectItem value="medium">Trung bình (++)</SelectItem>
                          <SelectItem value="high">Cao (+++)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="p-4 border rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Bot size={24} className="text-emerald-500" />
                        <div>
                          <Label className="text-base font-semibold">Tự động điền đầy kho</Label>
                          <p className="text-sm text-[#606770]">Tự động quét thêm link mới khi kho vơi đi</p>
                        </div>
                      </div>
                      <Switch 
                        checked={autoSearchSettings.auto_refill_pool || false}
                        onCheckedChange={(val) => setAutoSearchSettings(prev => ({ ...prev, auto_refill_pool: val }))}
                      />
                    </div>
                  </div>
                </>
              )}
              <Button
                onClick={handleUpdateAutoSettings}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingAutoSettings}
              >
                {savingAutoSettings ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                Lưu giới hạn & Keywords
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Source Pool Tab */}
        <TabsContent value="source-pool" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold">Nguồn Hotface dùng chung</CardTitle>
                  <CardDescription>
                    Admin có thể thêm các link Page/Group/Post tiềm năng vào đây để hệ thống tự động quét và trả về ngẫu nhiên cho người dùng.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <div className="flex items-center gap-3 px-4 py-2 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Tự động quét định kỳ</span>
                      <span className="text-[11px] text-emerald-600">Lịch: {autoSearchSettings?.auto_deep_scan_time?.[0] || 'Chưa đặt'}</span>
                    </div>
                    <Switch 
                      checked={(autoSearchSettings && autoSearchSettings.auto_deep_scan_enabled) || false}
                      onCheckedChange={(val) => {
                        const newSettings = { ...autoSearchSettings, auto_deep_scan_enabled: val };
                        setAutoSearchSettings(newSettings);
                        handleUpdateAutoSettings(newSettings);
                      }}
                    />
                    
                    <div className="h-6 w-px bg-emerald-200 mx-1" />
                    
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="poolAiEnabled"
                        checked={siteSettings.auto_scan_pool_ai}
                        onCheckedChange={(v) => handleUpdateSiteSettings('auto_scan_pool_ai', v)}
                      />
                      <label htmlFor="poolAiEnabled" className="text-[10px] font-bold text-emerald-700 cursor-pointer">SỬ DỤNG AI</label>
                    </div>
                    <Select 
                      value={(autoSearchSettings && autoSearchSettings.auto_deep_scan_time?.[0]) || '02:00'} 
                      onValueChange={(val) => {
                        const newSettings = { ...autoSearchSettings, auto_deep_scan_time: [val] };
                        setAutoSearchSettings(newSettings);
                        handleUpdateAutoSettings(newSettings);
                      }}
                    >
                      <SelectTrigger className="w-20 h-8 text-xs bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }).map((_, i) => {
                          const h = i.toString().padStart(2, '0');
                          return (
                            <SelectItem key={h} value={`${h}:00`}>{h}:00</SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleCheckPoolStatus}
                    disabled={checkingPoolStatus || sourcePool.length === 0}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  >
                    {checkingPoolStatus ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
                    Quét & Phân loại chất lượng {siteSettings.auto_scan_pool_ai ? '(AI)' : ''}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleResetPoolUsage}
                    disabled={checkingPoolStatus || sourcePool.length === 0}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  >
                     <RefreshCw size={16} className="mr-2" />
                    Reset lượt dùng
                  </Button>
                  <label htmlFor="import-source-txt">
                    <input
                      id="import-source-txt"
                      type="file"
                      accept=".txt"
                      onChange={handleImportSourceTxt}
                      className="hidden"
                      disabled={importingSource}
                    />
                    <Button
                      variant="outline"
                      asChild
                      disabled={importingSource}
                    >
                      <span className="cursor-pointer flex items-center">
                        {importingSource ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                        Import .txt
                      </span>
                    </Button>
                  </label>
                  <Button
                    onClick={handleRunPoolScan}
                    disabled={runningPoolScan}
                    className="bg-[#E41E3F] hover:bg-[#C01B35] animate-pulse"
                    title="Tìm bài viết HOT từ Facebook và tự động thêm vào nguồn chung"
                  >
                    {runningPoolScan ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
                    Quét bài hot (Magic Pool)
                  </Button>
                  <Button
                    onClick={() => setSourceDialogOpen(true)}
                    className="bg-[#1877F2] hover:bg-[#166FE5]"
                  >
                    <Plus size={16} className="mr-2" />
                    Thêm nguồn
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {sourcePool.length > 0 ? (
                <div className="space-y-3">
                  {sourcePool.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-[#F7F8FA] rounded-xl border border-transparent hover:border-[#1877F2] transition-all gap-4"
                    >
                      <div className="flex items-center gap-4 min-w-0 w-full md:w-auto">
                        <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shadow-sm", 
                          item.engagement_level === 'high' ? "bg-emerald-100" : 
                          item.engagement_level === 'medium' ? "bg-amber-100" : "bg-gray-100")}>
                          {item.engagement_level === 'high' ? <Sparkles size={20} className="text-emerald-600" /> : 
                           item.engagement_level === 'medium' ? <Zap size={20} className="text-amber-600" /> : 
                           <Search size={20} className="text-gray-400" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex flex-col">
                              <p className="font-bold text-sm text-[#1C1E21] truncate max-w-[200px] md:max-w-[400px]">
                                {item.page_name || (item.url.length > 30 ? item.url.substring(0, 30) + "..." : item.url)}
                              </p>
                              {item.is_processing && (
                                <span className="text-[10px] text-emerald-600 flex items-center gap-1 animate-pulse">
                                  <Loader2 className="w-3 h-3 animate-spin" /> Bot đang quét...
                                </span>
                              )}
                            </div>
                            
                            {/* AI ENGAGEMENT LABELS */}
                            {item.engagement_level === 'high' && (
                              <Badge className="bg-emerald-500 text-white border-0 text-[10px] h-5 px-1.5 shadow-sm">
                                CAO
                              </Badge>
                            )}
                            {item.engagement_level === 'medium' && (
                              <Badge className="bg-amber-500 text-white border-0 text-[10px] h-5 px-1.5 shadow-sm">
                                TRUNG BÌNH
                              </Badge>
                            )}
                            {item.engagement_level === 'low' && (
                              <Badge variant="secondary" className="bg-gray-300 text-gray-700 border-0 text-[10px] h-5 px-1.5">
                                THẤP
                              </Badge>
                            )}

                            {item.is_die ? (
                              <Badge variant="destructive" className="shrink-0 text-[10px] h-5 animate-pulse">CHẾT (DIE)</Badge>
                            ) : (
                              item.last_checked && <Badge variant="outline" className="shrink-0 text-[10px] h-5 bg-green-50 text-green-700 border-green-200">LIVE</Badge>
                            )}
                          </div>
                          <p className="font-mono text-[11px] text-[#606770] truncate max-w-[250px] md:max-w-[400px] mt-0.5">{item.url}</p>
                          
                          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-[#606770]">
                            <div className="flex items-center gap-1 bg-white px-1.5 py-0.5 rounded border shadow-sm">
                              <Users size={12} className="text-blue-500" />
                              <span className="font-semibold">Sử dụng: {(item.used_by_users?.length || 0)}</span>
                            </div>
                              {item.likes > 0 && (
                                <div className="flex items-center gap-1">
                                  <ThumbsUp size={12} className="text-blue-500" />
                                  <span>{item.likes.toLocaleString()} Like</span>
                                </div>
                              )}
                              {item.reels_views > 0 && (
                                <div className="flex items-center gap-1 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100 shadow-sm">
                                  <Bot size={12} className="text-purple-500" />
                                  <span className="font-semibold text-purple-700">{item.reels_views.toLocaleString()} Views</span>
                                </div>
                              )}
                              <span>Thêm: {new Date(item.added_at).toLocaleDateString('vi-VN')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteSource(item.id)}
                          className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center flex flex-col items-center justify-center text-[#606770]">
                  <div className="w-16 h-16 bg-[#F0F2F5] rounded-full flex items-center justify-center mb-4">
                    <Search size={32} className="text-[#90949C]" />
                  </div>
                  <p className="font-semibold text-lg">Chưa có nguồn chung nào</p>
                  <p className="text-sm max-w-xs mt-2">Nguồn chung giúp hệ thống trả về kết quả nhanh hơn cho người dùng khi họ không có watchlist riêng.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Site Settings Tab */}
        <TabsContent value="site-settings" className="mt-6">
          <Card data-testid="site-settings-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Cài đặt trang & Liên hệ</CardTitle>
              <CardDescription>
                Cấu hình thông tin hiển thị trên trang Giới thiệu (About)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tên trang</Label>
                  <Input
                    value={siteSettings.site_name || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, site_name: e.target.value }))}
                    placeholder="Hotface Analytics"
                    data-testid="site-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Logo URL</Label>
                  <Input
                    value={siteSettings.logo_url || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                    data-testid="logo-url-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tên nhãn Sidebar (Subtitle)</Label>
                  <Input
                    value={siteSettings.site_subname || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, site_subname: e.target.value }))}
                    placeholder="Analytics"
                    data-testid="site-subname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tiêu đề trình duyệt (Tab Title)</Label>
                  <Input
                    value={siteSettings.browser_title || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, browser_title: e.target.value }))}
                    placeholder="TriTechz - Solutions"
                    data-testid="browser-title-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mô tả trang</Label>
                <Textarea
                  value={siteSettings.about_text || ''}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, about_text: e.target.value }))}
                  placeholder="Mô tả ngắn về dịch vụ..."
                  rows={3}
                  data-testid="about-text-input"
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#1C1E21] mb-4">Kênh liên hệ</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MessageCircle size={14} className="text-[#0068FF]" />
                      Link Zalo
                    </Label>
                    <Input
                      value={siteSettings.zalo_link || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, zalo_link: e.target.value }))}
                      placeholder="https://zalo.me/..."
                      data-testid="zalo-link-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe size={14} className="text-[#1877F2]" />
                      Link Facebook
                    </Label>
                    <Input
                      value={siteSettings.facebook_link || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, facebook_link: e.target.value }))}
                      placeholder="https://facebook.com/..."
                      data-testid="facebook-link-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe size={14} className="text-[#0088CC]" />
                      Link Telegram
                    </Label>
                    <Input
                      value={siteSettings.telegram_link || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, telegram_link: e.target.value }))}
                      placeholder="https://t.me/..."
                      data-testid="telegram-link-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone size={14} className="text-[#31A24C]" />
                      Hotline
                    </Label>
                    <Input
                      value={siteSettings.hotline || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, hotline: e.target.value }))}
                      placeholder="0912345678"
                      data-testid="hotline-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Zap size={14} className="text-blue-500" />
                      Telegram Bot Token (Thông báo)
                    </Label>
                    <Input
                      type="password"
                      value={siteSettings.telegram_bot_token || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, telegram_bot_token: e.target.value }))}
                      placeholder="123456789:ABCdefGHIjklmNOPqrstUVWxyz"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users size={14} className="text-blue-500" />
                      Telegram Chat ID
                    </Label>
                    <Input
                      value={siteSettings.telegram_chat_id || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, telegram_chat_id: e.target.value }))}
                      placeholder="-100123456789"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Bản quyền chân trang (Copyright)</Label>
                  <Input
                    value={siteSettings.footer_copyright || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, footer_copyright: e.target.value }))}
                    placeholder="@ 2026 TriTechz. All rights reserved."
                    data-testid="footer-copyright-input"
                  />
                  <p className="text-xs text-[#8C939D]">
                    Thông tin hiển thị ở dưới cùng của tất cả các trang. @copyriognt bản quyền tự điền bới TriTechz
                  </p>
                </div>
              </div>

              {/* Billing Mode Section */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#1C1E21] mb-4">Cài đặt nạp tiền</h4>
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-xl gap-4">
                    <div>
                      <Label className="text-base font-semibold">Chế độ nạp tiền</Label>
                      <p className="text-sm text-[#606770] mt-1">
                        {siteSettings.billing_mode === 'auto'
                          ? 'Nạp tự động - User tự nạp qua hệ thống'
                          : 'Nạp qua Admin - User liên hệ admin để nạp'}
                      </p>
                    </div>
                    <Select
                      value={siteSettings.billing_mode || 'manual'}
                      onValueChange={(value) => setSiteSettings(prev => ({ ...prev, billing_mode: value }))}
                    >
                      <SelectTrigger className="w-48" data-testid="billing-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Nạp qua Admin</SelectItem>
                        <SelectItem value="auto">Nạp tự động</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {siteSettings.billing_mode !== 'auto' && (
                    <div className="space-y-2">
                      <Label>Hướng dẫn nạp tiền (hiển thị cho người dùng)</Label>
                      <Textarea
                        value={siteSettings.billing_contact_info || ''}
                        onChange={(e) => setSiteSettings(prev => ({ ...prev, billing_contact_info: e.target.value }))}
                        placeholder={"VD:\nBước 1: Chuyển khoản theo thông tin bên dưới\nBước 2: Gửi ảnh chụp bill qua Zalo/Telegram\nBước 3: Admin sẽ cộng credits trong vòng 30 phút\n\nNgân hàng: Vietcombank\nSTK: 123456789\nChủ TK: Nguyen Van A"}
                        rows={6}
                        data-testid="billing-contact-info-input"
                      />
                      <p className="text-xs text-[#8C939D]">
                        Nội dung này + thông tin liên hệ (Zalo, FB, Telegram, Hotline) sẽ hiển thị trên trang Nạp tiền
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSaveSiteSettings}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingSiteSettings}
                data-testid="save-site-settings-btn"
              >
                {savingSiteSettings ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" />Đang lưu...</>
                ) : (
                  <><Save size={16} className="mr-2" />Lưu cài đặt trang</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements" className="mt-6">
          <Card data-testid="announcements-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold">Quản lý thông báo</CardTitle>
                  <CardDescription>
                    Tạo thông báo hiển thị cho tất cả người dùng khi đăng nhập
                  </CardDescription>
                </div>
                <Button onClick={() => setAnnDialogOpen(true)} className="bg-[#1877F2] hover:bg-[#166FE5] w-full sm:w-auto" data-testid="create-announcement-btn">
                  <Plus size={16} className="mr-2" />
                  Tạo thông báo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-8 text-[#8C939D]">
                  <Megaphone size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Chưa có thông báo nào</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {announcements.map((ann) => (
                    <div
                      key={ann.id}
                      className={`flex flex-col md:flex-row md:items-start justify-between gap-4 p-4 rounded-xl border ${ann.is_active ? 'bg-[#E7F3FF] border-[#1877F2]/20' : 'bg-[#F7F8FA] border-[#E4E6EB]'
                        }`}
                      data-testid={`admin-announcement-${ann.id}`}
                    >
                      <div className="flex items-start gap-4 w-full md:w-auto">
                        <Megaphone size={18} className={`shrink-0 mt-0.5 ${ann.is_active ? 'text-[#1877F2]' : 'text-[#8C939D]'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-[#1C1E21]">{ann.title}</p>
                            {ann.is_popup && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none font-semibold text-xs py-0">Nổi bật</Badge>}
                          </div>
                          <p className="text-sm text-[#606770] mt-1">{ann.content}</p>
                          <p className="text-xs text-[#8C939D] mt-2">
                            {new Date(ann.created_at).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap md:flex-nowrap items-center gap-2 mt-2 md:mt-0 justify-end md:justify-start w-full md:w-auto">
                        <Button
                          variant={ann.is_popup ? "secondary" : "outline"}
                          size="icon"
                          onClick={() => handleTogglePopupAnnouncement(ann.id, ann.is_popup)}
                          className={ann.is_popup 
                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200" 
                            : "text-amber-500 hover:text-amber-600 hover:bg-amber-50"}
                          title={ann.is_popup ? "Tắt thông báo nổi bật" : "Đặt làm thông báo nổi bật"}
                        >
                          <Crown size={16} />
                        </Button>
                        <Switch
                          checked={ann.is_active}
                          onCheckedChange={() => handleToggleAnnouncement(ann.id, ann.is_active)}
                          data-testid={`toggle-announcement-${ann.id}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteAnnouncement(ann.id)}
                          className="text-[#E41E3F] hover:bg-[#FCE9EC]"
                          data-testid={`delete-announcement-${ann.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Management Tab */}
        <TabsContent value="features" className="mt-6">
          <Card data-testid="features-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="text-[#1877F2]" />
                  Quản lý trạng thái tính năng
                </div>
                <div className="flex items-center gap-3">
                  {/* Mode Select */}
                  <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-2xl border border-blue-200 shadow-sm">
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-black text-blue-600 uppercase tracking-tighter">CƠ CHẾ CHECK HOT</span>
                    </div>
                    <Select
                      value={siteSettings.analysis_mode || 'auto'}
                      onValueChange={async (value) => {
                        try {
                          setSavingSiteSettings(true);
                          await adminAPI.updateSiteSettings({ analysis_mode: value });
                          setSiteSettings(prev => ({ ...prev, analysis_mode: value }));
                          toast.success(`Đã cập nhật cơ chế thành ${value === 'auto' ? 'Tự động' : 'Thủ công'}!`);
                        } catch (err) {
                          toast.error('Lỗi khi cập nhật cấu hình');
                        } finally {
                          setSavingSiteSettings(false);
                        }
                      }}
                    >
                      <SelectTrigger className="w-[140px] bg-white h-7 text-xs font-bold border-blue-200">
                        <SelectValue placeholder="Chọn cơ chế" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Tự động (AI)</SelectItem>
                        <SelectItem value="manual">Phê duyệt (Tay)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* GLOBAL AI REFRESH TOGGLE */}
                  <div className="flex items-center gap-3 px-4 py-2 bg-amber-50 rounded-2xl border border-amber-200 shadow-sm">
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-black text-amber-600 uppercase tracking-tighter">CHẾ ĐỘ ÉP QUÉT MỚI (AI)</span>
                      <span className="text-[9px] text-amber-500 font-medium italic">*Tốn tài nguyên AI</span>
                    </div>
                    <Switch 
                      checked={siteSettings.force_ai_refresh}
                      onCheckedChange={async (checked) => {
                        try {
                           setSavingSiteSettings(true);
                           await adminAPI.updateSiteSettings({ force_ai_refresh: checked });
                           setSiteSettings(prev => ({ ...prev, force_ai_refresh: checked }));
                           toast.success(`Đã ${checked ? 'BẬT' : 'TẮT'} chế độ ép quét AI toàn hệ thống!`);
                        } catch (err) {
                           toast.error('Lỗi khi cập nhật cấu hình');
                        } finally {
                           setSavingSiteSettings(false);
                        }
                      }}
                      className="data-[state=checked]:bg-amber-500"
                    />
                  </div>
                </div>
              </CardTitle>
              <CardDescription>
                Bật/Tắt các chức năng của người dùng và thiết lập thông báo bảo trì/cập nhật.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {featureFlags.map((flag) => (
                  <div key={flag.id} className="p-4 border rounded-xl space-y-4 bg-[#F8FAFC]">
                    <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${flag.is_enabled ? 'bg-[#E7F3FF] text-[#1877F2]' : 'bg-gray-100 text-gray-400'}`}>
                          {flag.id === 'auto_search' ? <Zap size={20} /> : 
                           flag.id === 'link_analysis' ? <Search size={20} /> : 
                           flag.id === 'market_trend' ? <Flame size={20} /> : 
                           flag.id === 'ai_content' ? <Sparkles size={20} /> : <FileText size={20} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Label className="text-base font-bold text-[#1C1E21]">{flag.name}</Label>
                            {flag.is_vip && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200 uppercase flex items-center gap-1">
                                <Crown size={10} />
                                VIP ONLY
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#606770] flex items-center gap-2 mt-0.5">
                            ID: <span className="font-mono">{flag.id}</span>
                            {flag.updated_at && (
                              <span className="text-gray-400 italic">| Cập nhật: {new Date(flag.updated_at).toLocaleString('vi-VN')}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 sm:gap-8">
                        {/* VIP Mode Toggle */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs font-semibold text-amber-600">CHẾ ĐỘ VIP:</Label>
                          <Switch
                            checked={flag.is_vip}
                            onCheckedChange={() => handleToggleVIPFeature(flag.id, flag.is_vip)}
                            disabled={savingFeatureFlags[flag.id]}
                          />
                        </div>

                        {/* Status Toggle (Maintenance) */}
                        <div className="flex items-center gap-3 border-l pl-6">
                          <span className={`text-xs font-bold uppercase ${flag.is_enabled ? 'text-green-600' : 'text-red-500'}`}>
                            {flag.is_enabled ? 'Đang hoạt động' : 'Bảo trì'}
                          </span>
                          <Switch
                            checked={flag.is_enabled}
                            onCheckedChange={() => handleToggleFeature(flag.id, flag.is_enabled)}
                            disabled={savingFeatureFlags[flag.id]}
                            data-testid={`toggle-feature-${flag.id}`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Notifications section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-dashed">
                      {/* Maintenance Message */}
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-[#606770] flex items-center gap-1">
                          <AlertCircle size={14} className="text-red-500" />
                          Thông báo bảo trì:
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            defaultValue={flag.message}
                            placeholder="Nhập thông báo khi bảo trì..."
                            className="flex-1 h-9 text-sm"
                            onBlur={(e) => {
                              if (e.target.value !== flag.message) {
                                handleUpdateFeatureMessage(flag.id, e.target.value);
                              }
                            }}
                          />
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-9 w-9 bg-white border text-blue-600 hover:bg-blue-50"
                            onClick={(e) => {
                              const input = e.currentTarget.previousSibling;
                              handleUpdateFeatureMessage(flag.id, input.value);
                            }}
                          >
                            <Save size={16} />
                          </Button>
                        </div>
                      </div>

                      {/* VIP Message */}
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                          <Crown size={14} />
                          Thông báo cho thành viên Thường:
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            defaultValue={flag.vip_message}
                            placeholder="Nhập thông báo khi là VIP ONLY..."
                            className="flex-1 h-9 text-sm"
                            onBlur={(e) => {
                              if (e.target.value !== flag.vip_message) {
                                handleUpdateFeatureVipMessage(flag.id, e.target.value);
                              }
                            }}
                          />
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-9 w-9 bg-white border text-amber-600 hover:bg-amber-50"
                            onClick={(e) => {
                              const input = e.currentTarget.previousSibling;
                              handleUpdateFeatureVipMessage(flag.id, input.value);
                            }}
                          >
                            <Save size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Market Trend Tab */}
        <TabsContent value="market-trend" className="mt-6 flex flex-col gap-6">
          {/* Edit Nhanh Card */}
          <Card className="border-[#E74C3C]/20 shadow-sm overflow-hidden">
            <div className="h-1 bg-[#E74C3C]"></div>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Edit3 size={18} className="text-[#E74C3C]" />
                Edit Nhanh Market Trend Hiện Tại
              </CardTitle>
              <CardDescription>
                Thay đổi nhanh điểm và trạng thái hiển thị cho người dùng.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-3 bg-blue-50 border border-blue-100 rounded-lg mb-4">
                <Info size={18} className="text-blue-500 flex-shrink-0" />
                <div className="text-sm">
                  <span className="text-gray-600">Hiện tại hệ thống đánh giá:</span>{" "}
                  <strong className="text-blue-700">{editTrendScore} điểm</strong>{" "}
                  <span className="text-gray-400">|</span>{" "}
                  <span className="text-gray-600">Trạng thái:</span>{" "}
                  <span className={`font-bold ${editTrendStatus === 'Tốt' ? 'text-green-600' :
                      editTrendStatus === 'Bình thường' ? 'text-blue-600' :
                        editTrendStatus === 'Hơi kém' ? 'text-orange-500' : 'text-red-600'
                    }`}>
                    {editTrendStatus}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Điểm Market Trend (0-100)</Label>
                  <Input
                    type="number"
                    value={editTrendScore}
                    onChange={(e) => setEditTrendScore(e.target.value)}
                    max={100}
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Trạng thái hiển thị</Label>
                  <Select value={editTrendStatus} onValueChange={setEditTrendStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tốt">Tốt</SelectItem>
                      <SelectItem value="Bình thường">Bình thường</SelectItem>
                      <SelectItem value="Hơi kém">Hơi kém</SelectItem>
                      <SelectItem value="Kém">Kém</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={async () => {
                  try {
                    setIsEditingTrend(true);
                    await adminAPI.editLatestMarketTrend({
                      avg_score: parseFloat(editTrendScore),
                      status: editTrendStatus
                    });
                    toast.success('Đã cập nhật Market Trend hiển thị!');
                  } catch (e) {
                    toast.error('Cập nhật thất bại');
                  } finally {
                    setIsEditingTrend(false);
                  }
                }}
                disabled={isEditingTrend}
                className="w-full bg-[#E74C3C] hover:bg-[#C0392B]"
              >
                {isEditingTrend ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                Lưu Thay Đổi Hiển Thị
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Flame className="text-[#E74C3C]" />
                    Cấu Hình Tự Động Đánh Giá
                  </CardTitle>
                  <CardDescription>
                    Thiết lập các tiêu chí và ngưỡng để AI tự động phân tích thị trường.
                  </CardDescription>
                </div>
                <Button onClick={handleRunMarketTrend} disabled={runningMarketTrend} className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 w-full sm:w-auto">
                  {runningMarketTrend ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
                  {marketTrendAiEnabled ? 'Yêu cầu AI quét lại ngay' : 'Quét & Cập nhật xu hướng ngay'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Danh sách URLs thước đo (mỗi link 1 dòng)</Label>
                <Textarea
                  value={marketTrendUrls}
                  onChange={(e) => setMarketTrendUrls(e.target.value)}
                  placeholder="https://facebook.com/groups/... &#10;https://facebook.com/pages/..."
                  rows={6}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Like Tối Thiểu</Label>
                  <Input
                    type="number"
                    value={marketTrendMinLikes}
                    onChange={(e) => setMarketTrendMinLikes(e.target.value)}
                    placeholder="100"
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Comment Tối Thiểu</Label>
                  <Input
                    type="number"
                    value={marketTrendMinComments}
                    onChange={(e) => setMarketTrendMinComments(e.target.value)}
                    placeholder="10"
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Share Tối Thiểu</Label>
                  <Input
                    type="number"
                    value={marketTrendMinShares}
                    onChange={(e) => setMarketTrendMinShares(e.target.value)}
                    placeholder="5"
                    min={0}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Quy định một bài viết được tính là "Tốt" nếu đạt đủ 3 tiêu chí trên.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t pt-4">
                <div className="space-y-2">
                  <Label>% Đạt để là TỐT</Label>
                  <Input
                    type="number"
                    value={marketTrendGoodPercent}
                    onChange={(e) => setMarketTrendGoodPercent(e.target.value)}
                    placeholder="50"
                    min={0}
                    max={100}
                  />
                  <p className="text-[10px] text-gray-400">&ge; {marketTrendGoodPercent}% ➡️ Tốt</p>
                </div>
                <div className="space-y-2">
                  <Label>% Đạt để BÌNH THƯỜNG</Label>
                  <Input
                    type="number"
                    value={marketTrendMediumPercent}
                    onChange={(e) => setMarketTrendMediumPercent(e.target.value)}
                    placeholder="30"
                    min={0}
                    max={100}
                  />
                  <p className="text-[10px] text-gray-400">&ge; {marketTrendMediumPercent}% ➡️ Bình thường</p>
                </div>
                <div className="space-y-2">
                  <Label>% Đạt để HƠI KÉM</Label>
                  <Input
                    type="number"
                    value={marketTrendLowPercent}
                    onChange={(e) => setMarketTrendLowPercent(e.target.value)}
                    placeholder="10"
                    min={0}
                    max={100}
                  />
                  <p className="text-[10px] text-gray-400">&ge; {marketTrendLowPercent}% ➡️ Hơi kém. Nhỏ hơn ➡️ Kém</p>
                </div>
                <div className="space-y-2">
                  <Label>Số bài/link</Label>
                  <Input
                    type="number"
                    value={marketTrendPostsPerLink}
                    onChange={(e) => setMarketTrendPostsPerLink(e.target.value)}
                    min={1}
                    max={10}
                  />
                  <p className="text-[10px] text-gray-400">Cào X bài mới nhất</p>
                </div>
              </div>

              <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Clock size={16} /> Chế độ đánh giá
                </Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assessmentMode"
                      value="scheduled"
                      checked={marketTrendAssessmentMode === 'scheduled'}
                      onChange={() => setMarketTrendAssessmentMode('scheduled')}
                      className="w-4 h-4 text-[#1877F2]"
                    />
                    <span className="text-sm">Tự động theo mốc giờ</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assessmentMode"
                      value="manual"
                      checked={marketTrendAssessmentMode === 'manual'}
                      onChange={() => setMarketTrendAssessmentMode('manual')}
                      className="w-4 h-4 text-[#1877F2]"
                    />
                    <span className="text-sm">Admin tự chạy thủ công</span>
                  </label>
                </div>
              </div>

              {marketTrendAssessmentMode === 'scheduled' && (
                <div className="space-y-2 p-3 bg-[#f8f9fa] rounded-lg border border-dashed">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Clock size={14} className="text-[#1877F2]" />
                      Lịch theo dõi tự động (Hàng ngày)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="time"
                        value={newCheckTime}
                        onChange={(e) => setNewCheckTime(e.target.value)}
                        className="w-32 h-8"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          if (newCheckTime && !marketTrendCheckTimes.includes(newCheckTime)) {
                            setMarketTrendCheckTimes([...marketTrendCheckTimes, newCheckTime].sort());
                            setNewCheckTime('');
                          }
                        }}
                        className="h-8"
                      >
                        Thêm mốc
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {marketTrendCheckTimes.length === 0 ? (
                      <span className="text-sm text-gray-400 italic">Chưa có lịch chạy tự động.</span>
                    ) : (
                      marketTrendCheckTimes.map(time => (
                        <div key={time} className="bg-white px-2 py-1 rounded-md flex items-center gap-2 text-sm border shadow-sm">
                          <span className="font-medium text-[#1877F2]">{time}</span>
                          <button
                            onClick={() => setMarketTrendCheckTimes(marketTrendCheckTimes.filter(t => t !== time))}
                            className="text-red-400 hover:text-red-600 font-bold transition-colors"
                          >
                            &times;
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-[10px] text-blue-500 mt-1">
                    * Hệ thống sẽ thông báo "Lần quét tiếp theo" cho người dùng dựa trên lịch này.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 p-3 bg-[#F0F2F5] rounded-lg">
                <input
                  type="checkbox"
                  id="aiEnabled"
                  checked={marketTrendAiEnabled}
                  onChange={(e) => setMarketTrendAiEnabled(e.target.checked)}
                  className="w-4 h-4 rounded text-[#1877F2]"
                />
                <Label htmlFor="aiEnabled" className="cursor-pointer">Sử dụng AI phân tích sâu (Vibe check)</Label>
              </div>

              <Button onClick={handleSaveMarketTrend} disabled={savingMarketTrend} className="w-full bg-[#1877F2] hover:bg-[#166FE5]">
                {savingMarketTrend ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                Lưu cấu hình & Chạy đánh giá ngay
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vip-config" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Cấu hình gói VIP</CardTitle>
              <CardDescription>
                Thiết lập giá tiền và thời hạn mặc định cho gói thành viên VIP
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Giá gói VIP (VNĐ)</Label>
                  <Input
                    type="number"
                    value={vipPrice}
                    onChange={(e) => setVipPrice(e.target.value)}
                    placeholder="500000"
                  />
                  <p className="text-xs text-[#606770]">Số tiền người dùng cần thanh toán để kích hoạt VIP</p>
                </div>
                <div className="space-y-2">
                  <Label>Thời hạn mặc định (Ngày)</Label>
                  <Input
                    type="number"
                    value={vipExpiryDays}
                    onChange={(e) => setVipExpiryDays(e.target.value)}
                    placeholder="30"
                  />
                  <p className="text-xs text-[#606770]">Số ngày sử dụng sau khi kích hoạt</p>
                </div>
              </div>
              <Button
                onClick={handleSaveVipConfig}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingVipConfig}
              >
                {savingVipConfig ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Lưu cấu hình VIP
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- MAINTENANCE TAB --- */}
        <TabsContent value="maintenance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2 text-red-600">
                <Trash2 size={18} />
                Quản lý & Bảo trì dữ liệu
              </CardTitle>
              <CardDescription>Theo dõi dung lượng và dọn dẹp dữ liệu cũ để tối ưu hệ thống</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {maintenanceStats ? Object.entries(maintenanceStats).sort((a,b) => b[1].count - a[1].count).map(([col, data]) => (
                  <div 
                    key={col} 
                    className="p-4 bg-white rounded-xl border flex flex-col gap-1 shadow-sm hover:border-[#1877F2]/30 transition-all cursor-pointer group"
                    onClick={() => {
                        const tabMap = {
                            'analyzed_links': 'maintenance',
                            'notification_logs': 'logs',
                            'market_trend_records': 'market-trend',
                            'hotface_links': 'source-pool',
                            'users': 'users',
                            'fb_cookies': 'cookies',
                            'ai_content_history': 'overview',
                            'transactions': 'overview',
                            'admin_source_pool': 'source-pool',
                            'auto_search_results': 'discovery-results',
                            'system_action_logs': 'system-logs',
                            'auto_search_logs': 'discovery-results'
                        };
                        if (tabMap[col]) {
                            setActiveTab(tabMap[col]);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                    }}
                  >
                    <span className="text-[10px] font-black text-[#606770] uppercase truncate tracking-widest group-hover:text-[#1877F2]">
                        {
                            col === 'analyzed_links' ? 'Lịch sử phân tích' :
                            col === 'notification_logs' ? 'Nhật ký TBáo' :
                            col === 'market_trend_records' ? 'Dữ liệu FB Trend' :
                            col === 'hotface_links' ? 'Hotface đã lưu' :
                            col === 'users' ? 'Người dùng' :
                            col === 'fb_cookies' ? 'FB Cookies' :
                            col === 'ai_content_history' ? 'Content AI (History)' :
                            col === 'transactions' ? 'Giao dịch/Credits' : 
                            col === 'admin_source_pool' ? 'Nguồn chung Hotface' :
                            col === 'auto_search_results' ? 'Kho Tìm Hot (Discovery)' : 
                            col === 'livestream_data' ? 'Dữ liệu Livestream' : 
                            col === 'system_action_logs' ? 'Log Hệ thống (Admin)' :
                            col === 'auto_search_logs' ? 'Log Search (Auto)' :
                            col === 'temp_screenshots' ? 'Ảnh chụp lỗi (Search)' : col.replace(/_/g, ' ')
                        }
                    </span>
                    <div className="flex items-baseline gap-2 mt-1">
                       <span className="text-xl font-bold text-[#1C1E21] group-hover:scale-110 transition-transform origin-left">{data.count.toLocaleString()}</span>
                       <span className="text-xs text-[#8C939D]">bản ghi</span>
                    </div>
                    <span className="text-[10px] text-[#1877F2] font-semibold flex items-center gap-1 mt-1">
                        <Database size={10} /> {data.size_kb > 1024 ? (data.size_kb / 1024).toFixed(2) + ' MB' : data.size_kb + ' KB'}
                    </span>
                  </div>
                )) : (
                  <Button variant="outline" onClick={fetchMaintenanceStats} className="col-span-full py-8 border-dashed">
                    <RefreshCw size={24} className="mr-2" /> Tải thống kê dữ liệu hệ thống
                  </Button>
                )}
              </div>

              <div className="p-6 bg-red-50 rounded-2xl border border-red-100 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-red-900 flex items-center gap-2">
                    <AlertCircle size={18} /> Công cụ dọn dẹp (Cleanup Tool)
                  </h4>
                  <p className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded font-medium">Lưu ý: Hành động Xóa sẽ không thể hoàn tác</p>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]">1</div>
                      Chọn các bảng dữ liệu muốn dọn dẹp
                    </Label>
                    <div className="grid grid-cols-2 gap-3 p-4 bg-white rounded-xl border">
                      {[
                        { id: 'analyzed_links', name: 'Lịch sử phân tích' },
                        { id: 'notification_logs', name: 'Log thông báo' },
                        { id: 'market_trend_records', name: 'Bản ghi FB Trend' },
                        { id: 'hotface_links', name: 'Hotface đã lưu' },
                        { id: 'ai_content_history', name: 'Lịch sử Content AI' },
                        { id: 'transactions', name: 'Lịch sử giao dịch' },
                        { id: 'admin_source_pool', name: 'Nguồn chung Hotface' },
                        { id: 'auto_search_results', name: 'Kết quả Discovery' },
                        { id: 'system_action_logs', name: 'Log Hệ thống' },
                        { id: 'auto_search_logs', name: 'Log Tự động tìm' },
                        { id: 'temp_screenshots', name: 'Ảnh chụp lỗi (.png)' },
                        { id: 'livestream_data', name: 'Livestream (Video/Log)' }
                      ].map(item => (
                        <div key={item.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={`clean-${item.id}`} 
                            checked={cleanupCollections.includes(item.id)}
                            onCheckedChange={(checked) => {
                              if (checked) setCleanupCollections(prev => [...prev, item.id]);
                              else setCleanupCollections(prev => prev.filter(c => c !== item.id));
                            }}
                          />
                          <Label htmlFor={`clean-${item.id}`} className="text-sm cursor-pointer select-none">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                       <Label className="text-sm font-semibold flex items-center gap-2">
                         <div className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]">2</div>
                         Thiết lập độ tuổi dữ liệu
                       </Label>
                       <Select value={cleanupDays.toString()} onValueChange={(v) => setCleanupDays(parseInt(v))}>
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0" className="text-red-600 font-bold">Tất cả (Reset sạch)</SelectItem>
                            <SelectItem value="1">Cũ hơn 1 ngày</SelectItem>
                            <SelectItem value="3">Cũ hơn 3 ngày</SelectItem>
                            <SelectItem value="7">Cũ hơn 7 ngày (Một tuần)</SelectItem>
                            <SelectItem value="10">Cũ hơn 10 ngày</SelectItem>
                            <SelectItem value="15">Cũ hơn 15 ngày</SelectItem>
                            <SelectItem value="30">Cũ hơn 30 ngày (Một tháng)</SelectItem>
                          </SelectContent>
                       </Select>
                       <div className="p-3 bg-white rounded-lg border border-red-100 flex items-start gap-2">
                         <Info size={14} className="text-red-500 mt-0.5" />
                         <p className="text-[11px] text-[#606770] leading-relaxed">
                            {cleanupDays === 0 ? (
                                <span className="text-red-600 font-bold">CẢNH BÁO: Tất cả dữ liệu hiện tại trong các bảng đã chọn sẽ bị XÓA VĨNH VIỄN!</span>
                            ) : (
                                <>Hệ thống sẽ giữ lại dữ liệu mới nhất. Các bản ghi được tạo trước ngày <strong>{new Date(Date.now() - cleanupDays * 24 * 60 * 60 * 1000).toLocaleDateString('vi-VN')}</strong> sẽ bị xóa vĩnh viễn.</>
                            )}
                         </p>
                       </div>
                    </div>

                    <Button 
                      variant="destructive" 
                      className="w-full h-12 font-bold text-base shadow-lg shadow-red-200"
                      onClick={triggerCleanup}
                      disabled={cleaningUp || cleanupCollections.length === 0}
                    >
                      {cleaningUp ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Trash2 size={18} className="mr-2" />}
                      {cleaningUp ? 'Đang dọn dẹp...' : 'Xác nhận xóa dữ liệu'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-6">
                 <div>
                    <h4 className="font-bold text-[#1C1E21] flex items-center gap-2 mb-1">
                    <Users size={18} className="text-indigo-500" /> Quản lý dữ liệu theo Người dùng
                    </h4>
                    <p className="text-xs text-[#606770]">Theo dõi dung lượng dữ liệu phát sinh của từng người dùng để reset khi cần thiết.</p>
                 </div>

                 <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
                    <table className="w-full text-sm">
                       <thead className="bg-[#F8FAFC] text-[#64748B] text-[10px] font-black uppercase tracking-widest border-b">
                          <tr>
                             <th className="p-4 text-left">Người dùng</th>
                             <th className="p-4 text-center">Phân tích</th>
                             <th className="p-4 text-center">Đã lưu</th>
                             <th className="p-4 text-center">Discovery</th>
                             <th className="p-4 text-center">Tất cả</th>
                             <th className="p-4 text-right">Thao tác</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-gray-50">
                          {userUsageStats.length > 0 ? userUsageStats.map(u => (
                             <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                                <td className="p-4">
                                   <div className="flex flex-col">
                                      <span className="font-bold text-[#1C1E21]">{u.name || 'N/A'}</span>
                                      <span className="text-[10px] text-[#8C939D]">{u.email}</span>
                                   </div>
                                </td>
                                <td className="p-4 text-center">
                                   <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-mono font-bold text-[11px]">
                                      {u.counts.analyzed_links}
                                   </span>
                                </td>
                                <td className="p-4 text-center font-mono font-bold text-[11px] text-[#606770]">
                                   {u.counts.hotface_links}
                                </td>
                                <td className="p-4 text-center font-mono font-bold text-[11px] text-[#606770]">
                                   {u.counts.auto_search_results}
                                </td>
                                <td className="p-4 text-center">
                                   <Badge variant="outline" className="font-black border-indigo-200 text-indigo-700 bg-indigo-50">
                                      {u.total}
                                   </Badge>
                                </td>
                                <td className="p-4 text-right">
                                   <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 flex items-center gap-1 font-bold text-[11px]"
                                      disabled={resetingUser === u.id}
                                      onClick={() => handleResetUser(u.id, ['analyzed_links', 'hotface_links', 'auto_search_results', 'notification_logs', 'ai_content_history', 'transactions'])}
                                   >
                                      {resetingUser === u.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                                      RESET DỮ LIỆU
                                   </Button>
                                </td>
                             </tr>
                          )) : (
                             <tr>
                                <td colSpan="6" className="p-8 text-center text-[#8C939D] italic">Chưa có dữ liệu người dùng.</td>
                             </tr>
                          )}
                       </tbody>
                    </table>
                 </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-bold text-[#1C1E21] flex items-center gap-2 mb-4">
                  <Database size={18} className="text-[#1877F2]" />
                  Danh sách link đã phân tích gần đây
                </h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F0F2F5] text-[#606770] text-xs uppercase">
                            <tr>
                                <th className="p-3 text-left font-bold rounded-tl-lg">URL / Profile</th>
                                <th className="p-3 text-left font-bold">Ngày phân tích</th>
                                <th className="p-3 text-left font-bold">User</th>
                                <th className="p-3 text-left font-bold rounded-tr-lg">Kết quả</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {analyzedLinks.length > 0 ? analyzedLinks.map((link, idx) => (
                                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-3 max-w-[300px] truncate">
                                        <a href={link.url} target="_blank" rel="noreferrer" className="text-[#1877F2] font-medium hover:underline">
                                            {link.full_name || link.url}
                                        </a>
                                    </td>
                                    <td className="p-3 text-[#606770]">
                                        {new Date(link.analyzed_at).toLocaleString('vi-VN')}
                                    </td>
                                    <td className="p-3 text-[#8C939D]">
                                        <span 
                                            className="font-mono text-[10px] cursor-pointer hover:text-[#1877F2] transition-colors" 
                                            style={{ userSelect: 'all' }}
                                            title="Click to select/copy email"
                                        >
                                            {link.user_email || (link.user_id?.substring(0, 8) + '...')}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        <Badge variant="outline" className={`${link.is_hotface ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>
                                            {link.is_hotface ? 'Hotface' : 'Thường'}
                                        </Badge>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="4" className="p-10 text-center text-[#8C939D] italic">
                                        Chưa có dữ liệu link đã phân tích
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* Discovery Results Tab */}
        <TabsContent value="discovery-results" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <FileSearch className="text-indigo-500" />
                    Kho Tìm Hotface (Discovery results)
                  </CardTitle>
                  <CardDescription>
                    Danh sách các Hotface được tìm thấy tự động qua từ khóa cho các User. Admin có thể duyệt các Hotface chất lượng cao vào Nguồn chung.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchDiscoveryResults} disabled={loadingDiscovery}>
                      <RefreshCw size={14} className={`mr-2 ${loadingDiscovery ? 'animate-spin' : ''}`} /> Làm mới
                    </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0F2F5] text-[#606770] font-bold uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="p-4 text-left">Hotface</th>
                      <th className="p-4 text-center">Chất lượng</th>
                      <th className="p-4 text-right">Tương tác TB</th>
                      <th className="p-4 text-left">User</th>
                      <th className="p-4 text-right">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {discoveryResults.length > 0 ? discoveryResults.map(result => (
                      <tr key={result.id} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {result.avatar_url ? (
                              <img src={result.avatar_url} alt="" className="w-10 h-10 rounded-full border shadow-sm" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 font-bold border">
                                {result.page_name?.charAt(0) || <User size={16} />}
                              </div>
                            )}
                            <div className="min-w-0">
                              <a href={result.url} target="_blank" rel="noreferrer" className="font-bold text-[#1C1E21] hover:text-[#1877F2] truncate block">
                                {result.page_name || 'Facebook User'}
                              </a>
                              <div className="text-[10px] text-[#8C939D] flex items-center gap-2 mt-0.5">
                                 {result.followers && (
                                   <span className="bg-gray-100 px-1 rounded font-mono">{result.followers.toLocaleString()} fol</span>
                                 )}
                                 <span className="bg-blue-50 text-blue-600 px-1 rounded uppercase">{result.source}</span>
                                 <span className="opacity-60">{new Date(result.found_at).toLocaleDateString('vi-VN')}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                           <EngagementBadge level={result.engagement_level} score={result.engagement_score} />
                        </td>
                        <td className="p-4 text-right">
                           <div className="flex flex-col items-end gap-0.5">
                              <span className="text-xs font-bold text-[#1C1E21]">{result.likes?.toLocaleString()} likes</span>
                              <span className="text-[10px] text-[#606770]">{result.comments?.toLocaleString()} cmt • {result.shares?.toLocaleString()} share</span>
                           </div>
                        </td>
                        <td className="p-4">
                           <div className="text-xs font-medium text-[#606770] truncate max-w-[150px]" title={result.user_email}>
                              {result.user_email}
                           </div>
                        </td>
                        <td className="p-4 text-right">
                           <div className="flex items-center justify-end gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      size="icon" 
                                      variant="ghost" 
                                      className="h-8 w-8 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                                      onClick={() => handleApproveToPool(result)}
                                    >
                                      <Zap size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Duyệt vào Nguồn chung</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      size="icon" 
                                      variant="ghost" 
                                      className="h-8 w-8 text-red-500 hover:bg-red-50"
                                      onClick={() => handleDeleteDiscovery(result.id)}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Xóa kết quả</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                           </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" className="p-12 text-center text-[#8C939D] italic bg-gray-50/50">
                           <Sparkles size={48} className="mx-auto mb-3 opacity-20" />
                           <p>Chưa có Hotface nào được tìm thấy tự động.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system-logs">
          <Card className="border-none shadow-sm rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
              <div className="flex items-center gap-3">
                 <div className="p-2 bg-gray-100 rounded-xl text-gray-600">
                    <History size={20} />
                 </div>
                 <div>
                    <CardTitle className="text-lg font-bold">Nhật ký Hệ thống</CardTitle>
                    <CardDescription>Theo dõi các thay đổi cấu hình và thao tác quản trị</CardDescription>
                 </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-red-500 border-red-200 hover:bg-red-50 h-9 font-bold text-[11px]"
                onClick={handleClearSystemLogs}
              >
                <Trash2 size={14} className="mr-1.5" /> Xóa nhật ký
              </Button>
            </CardHeader>
            <CardContent className="p-0">
               {loadingLogs ? (
                 <div className="flex flex-col items-center justify-center p-20 gap-3">
                   <Loader2 size={32} className="animate-spin text-[#1877F2]" />
                   <p className="text-sm text-[#606770] animate-pulse">Đang tải nhật ký...</p>
                 </div>
               ) : (
                 <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                       <thead>
                          <tr className="bg-[#F7F8FA] border-b">
                             <th className="p-4 text-left font-bold text-[#606770] uppercase text-[10px] tracking-wider">Thời gian</th>
                             <th className="p-4 text-left font-bold text-[#606770] uppercase text-[10px] tracking-wider">Người thực hiện</th>
                             <th className="p-4 text-left font-bold text-[#606770] uppercase text-[10px] tracking-wider">Loại</th>
                             <th className="p-4 text-left font-bold text-[#606770] uppercase text-[10px] tracking-wider">Chi tiết thao tác</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-[#E4E6EB]">
                          {(systemLogs || []).map(log => (
                             <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="p-4 text-[#606770] font-medium text-[12px] whitespace-nowrap">
                                   <div className="flex items-center gap-2">
                                      <Clock size={12} className="text-gray-400" />
                                      {new Date(log.timestamp).toLocaleString('vi-VN')}
                                   </div>
                                </td>
                                <td className="p-4">
                                   <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600">
                                         {log.actor_name?.charAt(0)}
                                      </div>
                                      <span className="font-bold text-[#1C1E21]">{log.actor_name}</span>
                                   </div>
                                </td>
                                <td className="p-4">
                                   <Badge variant="secondary" className={`
                                      text-[10px] border-none font-extrabold uppercase py-0.5 px-2
                                      ${(log.action_type || '').startsWith('ADMIN') 
                                         ? 'bg-amber-50 text-amber-700' 
                                         : 'bg-blue-50 text-blue-700'}
                                   `}>
                                      {(log.action_type || '').replace('_', ' ')}
                                   </Badge>
                                </td>
                                <td className="p-4">
                                   <p className="text-[#1C1E21] leading-relaxed max-w-md">{log.details}</p>
                                </td>
                             </tr>
                          ))}
                          {(systemLogs || []).length === 0 && (
                             <tr>
                                <td colSpan="4" className="p-20 text-center">
                                   <div className="flex flex-col items-center gap-3">
                                      <div className="p-4 bg-gray-50 rounded-full text-gray-300">
                                         <History size={48} />
                                      </div>
                                      <p className="text-[#606770] italic">Chưa có bản ghi nhật ký nào được lưu lại.</p>
                                   </div>
                                </td>
                             </tr>
                          )}
                       </tbody>
                    </table>
                 </div>
               )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="livestream">
          <LivestreamManager />
        </TabsContent>

        <TabsContent value="analyze-requests">
          <Card>
            <CardHeader>
              <CardTitle>Yêu cầu Check Hot (Chờ duyệt)</CardTitle>
              <CardDescription>
                Danh sách các link được người dùng gửi yêu cầu phân tích trong chế độ Phê duyệt thủ công.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {analyzeRequests.length === 0 ? (
                <div className="text-center p-8 text-gray-500 border rounded-xl bg-gray-50/50">
                  Không có yêu cầu nào đang chờ duyệt.
                </div>
              ) : (
                <div className="space-y-4">
                  {analyzeRequests.map(req => (
                    <div key={req.id} className="p-4 border rounded-xl bg-white shadow-sm flex flex-col md:flex-row gap-4 justify-between md:items-center">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-800 break-all">{req.url}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Người dùng: {req.user_email} | Thời gian: {new Date(req.analyzed_at).toLocaleString('vi-VN')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button 
                          variant="outline" 
                          className="border-green-200 text-green-700 bg-green-50 hover:bg-green-100"
                          onClick={() => {
                            setApproveDialog({ open: true, request: req, processing: false });
                            setApproveForm({
                              engagement_level: 'low',
                              likes: 0,
                              comments: 0,
                              shares: 0,
                              followers: 0,
                              engagement_score: 0.0,
                              engagement_per_post: 60,
                              post_frequency: 70,
                              content_quality_score: 80,
                              page_name: '',
                              description: 'Phân tích và phê duyệt bởi Admin',
                              ai_summary: 'Hệ thống đã phân tích chi tiết hồ sơ mạng xã hội của bạn dựa trên dữ liệu tương tác thực tế.'
                            });
                          }}
                        >
                          <CheckCircle2 size={16} className="mr-2" /> Duyệt & Nhập KQ
                        </Button>
                        <Button 
                          variant="outline" 
                          className="border-rose-200 text-rose-700 hover:bg-rose-50"
                          onClick={() => handleRejectAnalyzeRequest(req.id)}
                        >
                          <Trash2 size={16} className="mr-2" /> Từ chối
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Cookie Dialog */}
      <Dialog open={cookieDialogOpen} onOpenChange={setCookieDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="add-cookie-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie size={20} className="text-[#1877F2]" />
              Thêm Cookie Facebook
            </DialogTitle>
            <DialogDescription>
              Thêm cookie từ tài khoản Facebook để crawl dữ liệu
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Chế độ thêm</Label>
              <div className="flex items-center gap-4">
                <button
                  className={`text-xs px-2 py-1 rounded ${!bulkMode ? 'bg-[#1877F2] text-white' : 'bg-gray-100 text-gray-600'}`}
                  onClick={() => setBulkMode(false)}
                >
                  Đơn lẻ
                </button>
                <button
                  className={`text-xs px-2 py-1 rounded ${bulkMode ? 'bg-[#1877F2] text-white' : 'bg-gray-100 text-gray-600'}`}
                  onClick={() => setBulkMode(true)}
                >
                  Hàng loạt
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cookieName">{bulkMode ? 'Tiền tố tên (Prefix)' : 'Tên cookie (để nhận diện)'}</Label>
              <Input
                id="cookieName"
                placeholder={bulkMode ? "VD: Cookie_Batch_1" : "VD: Nick chính, Account backup..."}
                value={newCookieName}
                onChange={(e) => setNewCookieName(e.target.value)}
                data-testid="cookie-name-input"
              />
            </div>

            {bulkMode && (
              <div className="space-y-2">
                <Label>Giới tính của dàn cookie này</Label>
                <Select value={cookieGender} onValueChange={setCookieGender}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Nam</SelectItem>
                    <SelectItem value="female">Nữ</SelectItem>
                    <SelectItem value="both">Cả hai / Không xác định</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="cookieString">{bulkMode ? 'Danh sách Cookie (mỗi dòng 1 cookie)' : 'Cookie string'}</Label>
                {!bulkMode && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-[#1877F2] hover:bg-[#E7F3FF]"
                    onClick={handleCheckSingleCookie}
                    disabled={checkingSingle}
                  >
                    {checkingSingle ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />}
                    Check Live
                  </Button>
                )}
              </div>
              <Textarea
                id="cookieString"
                placeholder={bulkMode ? "cookie1\ncookie2\ncookie3..." : "c_user=xxx; xs=xxx; datr=xxx; fr=xxx..."}
                value={newCookieString}
                onChange={(e) => {
                  setNewCookieString(e.target.value);
                  setIsCookieLive(null);
                }}
                rows={bulkMode ? 8 : 5}
                className="font-mono text-sm"
                data-testid="cookie-string-input"
              />
              {isCookieLive !== null && !bulkMode && (
                <p className={`text-xs mt-1 font-medium ${isCookieLive ? 'text-green-600' : 'text-red-500'}`}>
                  {isCookieLive ? '✓ Cookie hợp lệ' : '✗ Cookie đã chết hoặc không hợp lệ'}
                </p>
              )}
            </div>

            <div className="p-3 bg-[#FEF9E6] rounded-lg text-sm text-[#8F6702]">
              <strong>Lưu ý:</strong> {bulkMode ? 'Khi thêm hàng loạt, hệ thống sẽ tự động lọc bỏ các cookie đã chết.' : 'Cookie sẽ được lưu trữ an toàn. Tài khoản có thể bị khóa nếu Facebook phát hiện hoạt động bất thường.'}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCookieDialogOpen(false);
              setIsCookieLive(null);
            }} disabled={addingCookie}>
              Hủy
            </Button>
            <Button
              onClick={handleAddCookie}
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              disabled={addingCookie || (!bulkMode && isCookieLive === false)}
              data-testid="confirm-add-cookie-btn"
            >
              {addingCookie ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />Đang thêm...</>
              ) : (
                <><Plus size={16} className="mr-2" />{bulkMode ? 'Thêm hàng loạt' : 'Thêm Cookie'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Chỉnh sửa người dùng</DialogTitle>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-[#F7F8FA] rounded-lg">
                <p className="font-medium">{editingUser.name}</p>
                <p className="text-sm text-[#606770]">{editingUser.email}</p>
              </div>

              <div className="space-y-2">
                <Label>Vai trò</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Người dùng (User)</SelectItem>
                    <SelectItem value="admin">Quản trị viên (Admin)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Email nhận thông báo hoàn thành</Label>
                <Input
                  placeholder="admin@example.com, user2@gmail.com"
                  value={editNotificationEmail}
                  onChange={(e) => setEditNotificationEmail(e.target.value)}
                />
                <p className="text-xs text-[#606770]">
                  (Có thể nhập nhiều email cách nhau bởi dấu phẩy, dành cho nhận thông báo hoàn thành qua email)
                </p>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-xl bg-gray-50/50">
                <div className="space-y-0.5">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    {editIsLocked ? <Lock size={14} className="text-red-500" /> : <Unlock size={14} className="text-green-500" />}
                    Trạng thái tài khoản
                  </Label>
                  <p className="text-xs text-[#606770]">
                    {editIsLocked ? 'Tài khoản đang bị khóa' : 'Tài khoản đang hoạt động'}
                  </p>
                </div>
                <Switch
                  checked={editIsLocked}
                  onCheckedChange={setEditIsLocked}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {editingUser?.subscription_type === 'monthly' && (
              <Button 
                variant="destructive" 
                onClick={() => handleCancelVipClick(editingUser.id)} 
                disabled={saving}
                className="mr-auto"
              >
                Hủy gói VIP
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>Hủy</Button>
              <Button onClick={handleUpdateUser} className="bg-[#1877F2] hover:bg-[#166FE5]" disabled={saving} data-testid="save-user-btn">
                {saving ? <><Loader2 size={16} className="mr-2 animate-spin" />Đang lưu...</> : <><Save size={16} className="mr-2" />Lưu thay đổi</>}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Watchlist Dialog */}
      <Dialog open={watchlistDialogOpen} onOpenChange={setWatchlistDialogOpen}>
        <DialogContent data-testid="add-admin-watchlist-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe size={20} className="text-[#1877F2]" />
              Thêm Page/Group chung
            </DialogTitle>
            <DialogDescription>
              Thêm Fanpage hoặc Group để tất cả users có thể quét
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="watchName">Tên hiển thị</Label>
              <Input
                id="watchName"
                placeholder="VD: Trấn Thành Official"
                value={newWatchName}
                onChange={(e) => setNewWatchName(e.target.value)}
                data-testid="watch-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="watchUrl">URL Facebook</Label>
              <Input
                id="watchUrl"
                placeholder="https://www.facebook.com/..."
                value={newWatchUrl}
                onChange={(e) => setNewWatchUrl(e.target.value)}
                className="font-mono text-sm"
                data-testid="watch-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Loại</Label>
              <Select value={newWatchType} onValueChange={setNewWatchType}>
                <SelectTrigger data-testid="watch-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="page">Fanpage</SelectItem>
                  <SelectItem value="group">Group</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWatchlistDialogOpen(false)} disabled={addingWatchlist}>
              Hủy
            </Button>
            <Button
              onClick={handleAddWatchlist}
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              disabled={addingWatchlist}
              data-testid="confirm-add-watchlist-btn"
            >
              {addingWatchlist ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />Đang thêm...</>
              ) : (
                <><Plus size={16} className="mr-2" />Thêm</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Announcement Dialog */}
      <Dialog open={annDialogOpen} onOpenChange={setAnnDialogOpen}>
        <DialogContent data-testid="create-announcement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone size={20} className="text-[#1877F2]" />
              Tạo thông báo mới
            </DialogTitle>
            <DialogDescription>
              Thông báo sẽ hiển thị cho tất cả người dùng khi đăng nhập
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tiêu đề</Label>
              <Input
                value={newAnnTitle}
                onChange={(e) => setNewAnnTitle(e.target.value)}
                placeholder="VD: Bảo trì hệ thống..."
                data-testid="ann-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nội dung</Label>
              <Textarea
                value={newAnnContent}
                onChange={(e) => setNewAnnContent(e.target.value)}
                placeholder="Nội dung thông báo..."
                rows={4}
                data-testid="ann-content-input"
              />
            </div>
            <div className="space-y-2 pt-2 border-t">
              <div className="flex flex-row items-center space-x-2">
                <Checkbox
                  id="is_popup"
                  checked={newAnnIsPopup}
                  onCheckedChange={(checked) => setNewAnnIsPopup(checked)}
                  data-testid="is-popup-checkbox"
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="is_popup" className="cursor-pointer font-medium text-amber-600 flex items-center gap-1">
                    <Crown size={14} /> Chọn làm thông báo Nổi Bật (Popup)
                  </Label>
                  <p className="text-xs text-[#8C939D]">
                    Chỉ có tối đa 1 thông báo hiển thị dạng nổi bật cho tất cả người dùng khi đăng nhập.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnDialogOpen(false)} disabled={savingAnn}>Hủy</Button>
            <Button onClick={handleCreateAnnouncement} className="bg-[#1877F2] hover:bg-[#166FE5]" disabled={savingAnn} data-testid="confirm-create-ann-btn">
              {savingAnn ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />Đang tạo...</>
              ) : (
                <><Megaphone size={16} className="mr-2" />Tạo thông báo</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Source Dialog */}
      <Dialog open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Thêm nguồn Hotface dùng chung</DialogTitle>
            <DialogDescription>
              Nhập link Fanpage, Group hoặc bài viết cụ thể để hệ thống tự động phân tích và đưa vào kho dữ liệu.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sourceUrl">URL Facebook (Mỗi link một hàng)</Label>
              <Textarea
                id="sourceUrl"
                placeholder="https://facebook.com/page1&#10;https://facebook.com/group2&#10;https://facebook.com/post3..."
                value={newSourceUrl}
                onChange={(e) => setNewSourceUrl(e.target.value)}
                className="h-32 font-mono text-sm"
              />
              <p className="text-xs text-[#606770]">
                Bạn có thể copy nhiều link và dán vào đây, hệ thống sẽ tự động tách chúng.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Giới tính (Dành cho tất cả các link phía trên)</Label>
              <Select value={newSourceGender} onValueChange={setNewSourceGender}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Cả hai / Không xác định</SelectItem>
                  <SelectItem value="male">Nam</SelectItem>
                  <SelectItem value="female">Nữ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sourceNote">Ghi chú (Tùy chọn)</Label>
              <Input
                id="sourceNote"
                placeholder="VD: Nguồn từ nhóm mẹ bỉm sữa..."
                value={newSourceNote}
                onChange={(e) => setNewSourceNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSourceDialogOpen(false)}>Hủy</Button>
            <Button
              onClick={handleAddSource}
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              disabled={addingSource}
            >
              {addingSource ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
              Thêm nguồn
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Topup Dialog */}
      <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="text-amber-500" />
              Kích hoạt / Gia hạn Gói VIP
            </DialogTitle>
            <DialogDescription>
              Người dùng: {topupUserTarget?.name} ({topupUserTarget?.email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Crown className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-900 uppercase tracking-tight">Kích hoạt Gói VIP (Tháng)</p>
                <p className="text-xs text-amber-700">Người dùng sẽ được sử dụng các tính năng được bảo vệ miễn phí.</p>
              </div>
            </div>

              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="topupStart">Ngày bắt đầu</Label>
                    <Input
                      id="topupStart"
                      type="date"
                      value={topupStartDate}
                      onChange={(e) => setTopupStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="topupExpiry">Thời hạn (ngày)</Label>
                    <Select value={topupExpiryDays.toString()} onValueChange={(v) => setTopupExpiryDays(parseInt(v))}>
                      <SelectTrigger id="topupExpiry">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 ngày (1 tuần)</SelectItem>
                        <SelectItem value="30">30 ngày (1 tháng)</SelectItem>
                        <SelectItem value="90">90 ngày (3 tháng)</SelectItem>
                        <SelectItem value="180">180 ngày (6 tháng)</SelectItem>
                        <SelectItem value="365">365 ngày (1 năm)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium flex items-center gap-2">
                    <Clock size={12} />
                    VIP sẽ hết hạn vào: 
                    <span className="font-bold underline ml-auto">
                      {topupStartDate && topupExpiryDays ? (
                        new Date(new Date(topupStartDate).getTime() + topupExpiryDays * 24 * 60 * 60 * 1000).toLocaleDateString('vi-VN')
                      ) : '-'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupDialogOpen(false)} disabled={performingTopup}>Hủy</Button>
            <Button
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              onClick={handleTopup}
              disabled={performingTopup || (topupType === 'credits' && !topupAmount)}
            >
              {performingTopup ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
              Xác nhận nạp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="text-amber-500" />
              Đổi mật khẩu người dùng
            </DialogTitle>
            <DialogDescription>
              Thiết lập mật khẩu mới cho tài khoản: <strong>{passwordUserTarget?.name}</strong> ({passwordUserTarget?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Mật khẩu mới</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Nhập mật khẩu ít nhất 6 ký tự..."
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
              Lưu ý: Mật khẩu sẽ được cập nhật ngay lập tức. Người dùng có thể sử dụng mật khẩu này để đăng nhập sau khi thay đổi.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} disabled={updatingPassword}>Hủy</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleUpdatePassword}
              disabled={updatingPassword || !newPassword}
            >
              {updatingPassword ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Key size={16} className="mr-2" />}
              Cập nhật mật khẩu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel VIP Confirmation Dialog */}
      <Dialog open={cancelVipDialogOpen} onOpenChange={setCancelVipDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Xác nhận hủy gói VIP</DialogTitle>
            <DialogDescription>
              Bạn có chắc chắn muốn hủy gói VIP của người dùng này? Hành động này không thể hoàn tác và quyền lợi VIP của thẻ sẽ bị thu hồi ngay lập tức.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelVipDialogOpen(false)} disabled={saving}>Hủy</Button>
            <Button variant="destructive" onClick={confirmCancelVip} disabled={saving}>
              {saving ? <><Loader2 size={16} className="mr-2 animate-spin" />Đang xử lý...</> : 'Xác nhận hủy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Cleanup Confirmation Dialog */}
      <Dialog open={cleanupConfirmOpen} onOpenChange={setCleanupConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle size={20} />
              Xác nhận dọn dẹp dữ liệu
            </DialogTitle>
            <DialogDescription>
              Hệ thống sẽ xóa vĩnh viễn dữ liệu của <strong>{cleanupCollections.length} bảng</strong> đã chọn có tuổi đời trên <strong>{cleanupDays} ngày</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-sm font-medium">
              Lưu ý: Hành động này không thể hoàn tác. Dữ liệu sẽ bị xóa hoàn toàn khỏi cơ sở dữ liệu.
            </div>
            <div className="space-y-1">
              <span className="text-xs text-[#606770] font-bold uppercase">Bảng sẽ dọn dẹp:</span>
              <div className="flex flex-wrap gap-2 pt-1 font-mono text-[10px]">
                {cleanupCollections.map(c => (
                  <Badge key={c} variant="outline" className="bg-white text-[#1C1E21] border-[#E4E6EB]">{c}</Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCleanupConfirmOpen(false)}>Hủy bỏ</Button>
            <Button variant="destructive" onClick={handleCleanup} className="animate-pulse shadow-lg shadow-red-200">
              <Trash2 size={16} className="mr-2" />
              Xác nhận xóa ngay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Approve Analyze Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(open) => !approveDialog.processing && setApproveDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duyệt yêu cầu: {approveDialog.request?.url}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 h-[60vh] overflow-y-auto px-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tên Page / Profile</Label>
                <Input 
                  value={approveForm.page_name} 
                  onChange={e => setApproveForm(prev => ({ ...prev, page_name: e.target.value }))}
                  placeholder="VD: Trấn Thành"
                />
              </div>
              <div className="space-y-2">
                <Label>Người theo dõi</Label>
                <Input type="number" value={approveForm.followers} onChange={e => setApproveForm(prev => ({ ...prev, followers: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Lượt Thích</Label>
                <Input type="number" value={approveForm.likes} onChange={e => setApproveForm(prev => ({ ...prev, likes: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Bình luận</Label>
                <Input type="number" value={approveForm.comments} onChange={e => setApproveForm(prev => ({ ...prev, comments: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Chia sẻ</Label>
                <Input type="number" value={approveForm.shares} onChange={e => setApproveForm(prev => ({ ...prev, shares: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>TT / Bài (%)</Label>
                <Input type="number" value={approveForm.engagement_per_post} onChange={e => setApproveForm(prev => ({ ...prev, engagement_per_post: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Tần suất (%)</Label>
                <Input type="number" value={approveForm.post_frequency} onChange={e => setApproveForm(prev => ({ ...prev, post_frequency: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>CL Nội dung (%)</Label>
                <Input type="number" value={approveForm.content_quality_score} onChange={e => setApproveForm(prev => ({ ...prev, content_quality_score: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Điểm Hot (Hot Score)</Label>
                <Input type="number" step="0.1" value={approveForm.engagement_score} onChange={e => setApproveForm(prev => ({ ...prev, engagement_score: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Mức độ</Label>
                <Select value={approveForm.engagement_level} onValueChange={val => setApproveForm(prev => ({ ...prev, engagement_level: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">Cao</SelectItem>
                    <SelectItem value="medium">Trung bình</SelectItem>
                    <SelectItem value="low">Thấp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nhận xét / Mô tả AI (Hiển thị cho user)</Label>
              <Textarea 
                value={approveForm.ai_summary} 
                onChange={e => setApproveForm(prev => ({ ...prev, ai_summary: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Mô tả nội bộ (Admin)</Label>
              <Input 
                value={approveForm.description} 
                onChange={e => setApproveForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, request: null, processing: false })} disabled={approveDialog.processing}>
              Hủy
            </Button>
            <Button onClick={handleApproveAnalyzeRequest} disabled={approveDialog.processing} className="bg-green-600 hover:bg-green-700">
              {approveDialog.processing ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
              Xác nhận Duyệt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
      {/* Custom Confirmation Popup */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={20} className="text-[#1877F2]" />
              Xác nhận thao tác
            </DialogTitle>
            <DialogDescription className="py-4 text-sm text-[#606770]">
              {confirmMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setConfirmDialogOpen(false)}>Hủy</Button>
            <Button 
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              onClick={() => {
                confirmAction?.();
                setConfirmDialogOpen(false);
              }}
            >
              Đồng ý thiết lập
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
